import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import MeasurementInfo from '../components/MeasurementInfo';
import { formatPatientNameForDisplay } from '../utils/patientNameUtils';

const MADE_TO_MEASURE_LENGTH_OPTIONS = ['AD', 'AF', 'AG', 'AGHB', 'AT', 'ATU', 'ATH', 'ATX', 'ATE', 'BT', 'B1T', 'CT', 'ET', 'FT'];
const MADE_TO_MEASURE_COMPRESSION_OPTIONS = ['1', '2', '3', '4'];
const OFF_THE_SHELF_LENGTH_OPTIONS = ['AD', 'AG', 'AT'];
const OFF_THE_SHELF_COMPRESSION_OPTIONS = ['1', '2', '3'];
const KNITTING_TO_BREI = {
    Round: 'BreiRond',
    'High Stiffness': 'BreiHS',
    Flat: 'BreiVlak',
};
const DEFAULT_ZM_TENSION = {
    cA: '+',
    cB: '+',
    cB1: '++',
    cC: '++',
    cD: '+',
};
const CONDITIONAL_ZM_FIELDS = {
    right: ['cBR', 'cB1R', 'cCR', 'cDR', 'cAR'],
    left: ['cBL', 'cB1L', 'cCL', 'cDL', 'cAL'],
};
const REQUIRED_CONDITIONAL_ZM_FIELDS = {
    right: ['cBR', 'cCR', 'cDR', 'cAR'],
    left: ['cBL', 'cCL', 'cDL', 'cAL'],
};
const CONDITIONAL_ZM_FIELD_SET = new Set([
    ...CONDITIONAL_ZM_FIELDS.right,
    ...CONDITIONAL_ZM_FIELDS.left,
]);
const REQUIRED_CONDITIONAL_ZM_FIELD_SET = new Set([
    ...REQUIRED_CONDITIONAL_ZM_FIELDS.right,
    ...REQUIRED_CONDITIONAL_ZM_FIELDS.left,
]);

const LegMeasuring = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, updateUserPreferences, hasFeatureAccess, isProUser } = useAuth();
    const [measurementId, setMeasurementId] = useState(location.state?.measurementId || null);
    const [patientId, setPatientId] = useState('');
    const [patientFirstName, setPatientFirstName] = useState('');
    const [patientLastName, setPatientLastName] = useState('');
    const [patientDetails, setPatientDetails] = useState({
        dateOfBirth: '',
        gender: '',
        address: '',
        phoneNumber: '',
        email: '',
        notes: '',
        prescribingDoctor: '',
    });
    const [currentStep, setCurrentStep] = useState(0);
    const [isReadOnly, setIsReadOnly] = useState(location.state?.isReadOnly || false);
    const [createdAt, setCreatedAt] = useState(location.state?.createdAt || null);

    const normalizeNotes = (value) => {
        if (typeof value !== 'string') return '';
        const normalized = value.trim().toLowerCase();
        return ['false', 'null', 'undefined'].includes(normalized) ? '' : value;
    };

    // Leg swap preference
    const [swapLegSides, setSwapLegSides] = useState(
        user?.preferences?.swapLegSides || false
    );

    // Measures order swap preference
    const [swapMeasuresOrder, setSwapMeasuresOrder] = useState(
        user?.preferences?.swapMeasuresOrder || false
    );

    // Measurement type (off-the-shelf or made-to-measure) - PRO feature
    const [measurementType, setMeasurementType] = useState(
        location.state?.measurementType || 'offTheShelf'
    );
    const canShowMadeToMeasureSwitch = hasFeatureAccess('made-to-measure') || isProUser() || user?.subscription === 'PRO';

    const isConditionalZmConfiguration = () => (
        (measurements.lengthType || lengthType) === 'AD'
        && (measurements.knitting || knitting) === 'Flat'
        && measurementType === 'offTheShelf'
    );

    const shouldShowZmInputs = (fieldId) => (
        measurementType === 'madeToMeasure'
        || (isConditionalZmConfiguration() && CONDITIONAL_ZM_FIELD_SET.has(fieldId))
    );

    const isConditionalZmRequired = (fieldId) => (
        isConditionalZmConfiguration() && REQUIRED_CONDITIONAL_ZM_FIELD_SET.has(fieldId)
    );

    const [measurements, setMeasurements] = useState(() => {
        const initialMeasurements = location.state?.measurements || {};
        // Add specification values if they exist in location.state
        if (location.state?.compressionClass) {
            initialMeasurements.compressionClass = location.state.compressionClass;
        }
        if (location.state?.lengthType) {
            initialMeasurements.lengthType = location.state.lengthType;
        }
        if (location.state?.knitting) {
            initialMeasurements.knitting = location.state.knitting;
        }
        // Always use notes from location.state when explicitly provided.
        // Both PatientInfo and LegMeasuring allow editing the same notes field,
        // and the user may navigate back and forth between them.  location.state.notes
        // always reflects the latest edit from either page, so it must take precedence
        // over the potentially stale value already inside the measurements object.
        // When location.state.notes is absent, leave initialMeasurements.notes as-is
        // (either the value from location.state.measurements, or undefined).  Keeping
        // it undefined (rather than forcing '') allows the initialisation effect below
        // to apply sessionStorage notes via the ?? operator — critical for brand-new
        // patients where navigate() is called without any state.
        if (location.state?.notes !== undefined) {
            initialMeasurements.notes = normalizeNotes(location.state.notes);
        }
        return initialMeasurements;
    });

    const [isOrdered, setIsOrdered] = useState(location.state?.ordered || false);
    const [selectedProduct, setSelectedProduct] = useState(location.state?.selectedProduct || null);
    const [orderNote, setOrderNote] = useState(
        location.state?.currentMeasurement?.order?.orderNote || ''
    );
    const [existingOrder, setExistingOrder] = useState(
        location.state?.currentMeasurement?.order || {}
    );
    const [measurements2, setMeasurements2] = useState(location.state?.measurements2 || {});
    const [vendorTables, setVendorTables] = useState([]);
    const [selectedVendorName, setSelectedVendorName] = useState(location.state?.selectedProduct?.vendorName || '');
    const [selectedVendorProductId, setSelectedVendorProductId] = useState(
        location.state?.selectedProduct?.vendorTableID || location.state?.selectedProduct?.modelId || ''
    );
    const [prefillVendorType, setPrefillVendorType] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [currentMessage, setCurrentMessage] = useState(null);
    const recognitionRef = useRef(null);
    const currentStepRef = useRef(currentStep);
    const inputFieldRefs = useRef({});
    const hasInitializedRef = useRef(false);
    const swapMeasuresInitializedRef = useRef(false);

    // Product specification selections
    const [lengthType, setLengthType] = useState(
        location.state?.lengthType || ''
    );
    const [compressionClass, setCompressionClass] = useState(
        location.state?.compressionClass || ''
    );
    const [knitting, setKnitting] = useState(
        location.state?.knitting || ''
    );

    // Active leg measurement for SVG diagram highlighting
    const [activeLegMeasure, setActiveLegMeasure] = useState(null);
    // Active length measurement for SVG lengths diagram highlighting
    const [activeLengthMeasure, setActiveLengthMeasure] = useState(null);

    // Measurement workflow steps - Specifications first, then hips, then left and right legs with feet
    const measurementSteps = useMemo(() => ([
        // Specifications first
        { id: 'lengthType', label: 'Length Type', infoKey: 'lengthType', unit: '', section: 'specifications', type: 'select', options: measurementType === 'madeToMeasure' ? MADE_TO_MEASURE_LENGTH_OPTIONS : OFF_THE_SHELF_LENGTH_OPTIONS },
        { id: 'compressionClass', label: 'Compression Class', infoKey: 'compressionClass', unit: '', section: 'specifications', type: 'select', options: measurementType === 'madeToMeasure' ? MADE_TO_MEASURE_COMPRESSION_OPTIONS : OFF_THE_SHELF_COMPRESSION_OPTIONS },
        { id: 'knitting', label: 'Knitting', infoKey: 'knitting', unit: '', section: 'specifications', type: 'select', options: ['Round', 'High Stiffness', 'Flat'] },
        { id: 'notes', label: 'Notes', infoKey: 'notes', unit: '', section: 'specifications', type: 'textarea', optional: true },

        // Hip measurements
        { id: 'cT', label: 'cT', infoKey: 'cT', unit: 'cm', section: 'hip', optional: true },
        { id: 'cH', label: 'cH', infoKey: 'cH', unit: 'cm', section: 'hip', optional: true },

        // Right leg measurements (top to bottom)
        { id: 'cGR', label: 'cG', infoKey: 'cG', unit: 'cm', section: 'right' },
        { id: 'cFR', label: 'cF', infoKey: 'cF', unit: 'cm', section: 'right' },
        { id: 'cER', label: 'cE', infoKey: 'cE', unit: 'cm', section: 'right' },
        { id: 'cDR', label: 'cD', infoKey: 'cD', unit: 'cm', section: 'right' },
        { id: 'cCR', label: 'cC', infoKey: 'cC', unit: 'cm', section: 'right' },
        { id: 'cB1R', label: 'cB1', infoKey: 'cB1', unit: 'cm', section: 'right', optional: true },
        { id: 'cBR', label: 'cB', infoKey: 'cB', unit: 'cm', section: 'right' },

        // Right foot measurements
        { id: 'cYR', label: 'cY', infoKey: 'cY', unit: 'cm', section: 'rightFoot', optional: true },
        { id: 'cAR', label: 'cA', infoKey: 'cA', unit: 'cm', section: 'rightFoot', optional: true },
        /*         
                { id: 'lAR', label: 'lA', infoKey: 'lA', unit: 'cm', section: 'rightFoot', optional: true },
                { id: 'lZR', label: 'lZ', infoKey: 'lZ', unit: 'cm', section: 'rightFoot', optional: true },
                { id: 'sSR', label: 'sS', infoKey: 'sS', unit: 'EU Size', section: 'rightFoot', optional: true },
         */
        // Left leg measurements (top to bottom)
        { id: 'cGL', label: 'cG', infoKey: 'cG', unit: 'cm', section: 'left' },
        { id: 'cFL', label: 'cF', infoKey: 'cF', unit: 'cm', section: 'left' },
        { id: 'cEL', label: 'cE', infoKey: 'cE', unit: 'cm', section: 'left' },
        { id: 'cDL', label: 'cD', infoKey: 'cD', unit: 'cm', section: 'left' },
        { id: 'cCL', label: 'cC', infoKey: 'cC', unit: 'cm', section: 'left' },
        { id: 'cB1L', label: 'cB1', infoKey: 'cB1', unit: 'cm', section: 'left', optional: true },
        { id: 'cBL', label: 'cB', infoKey: 'cB', unit: 'cm', section: 'left' },

        // Left foot measurements
        { id: 'cYL', label: 'cY', infoKey: 'cY', unit: 'cm', section: 'leftFoot', optional: true },
        { id: 'cAL', label: 'cA', infoKey: 'cA', unit: 'cm', section: 'leftFoot', optional: true },

        /*         { id: 'lAL', label: 'lA', infoKey: 'lA', unit: 'cm', section: 'leftFoot', optional: true },
                { id: 'lZL', label: 'lZ', infoKey: 'lZ', unit: 'cm', section: 'leftFoot', optional: true },
                { id: 'sSL', label: 'sS', infoKey: 'sS', unit: 'EU Size', section: 'leftFoot', optional: true },
         */
        // leg length measurements
        { id: 'lT', label: 'lT', infoKey: 'lT', unit: 'cm', section: 'length', optional: true },
        { id: 'lH', label: 'lH', infoKey: 'lH', unit: 'cm', section: 'length', optional: true },
        { id: 'lG', label: 'lG', infoKey: 'lG', unit: 'cm', section: 'length', optional: true },
        { id: 'lF', label: 'lF', infoKey: 'lF', unit: 'cm', section: 'length', optional: true },
        { id: 'lE', label: 'lE', infoKey: 'lE', unit: 'cm', section: 'length', optional: true },
        { id: 'lD', label: 'lD', infoKey: 'lD', unit: 'cm', section: 'length', optional: true },
        { id: 'lC', label: 'lC', infoKey: 'lC', unit: 'cm', section: 'length', optional: true },
        { id: 'lB1', label: 'lB1', infoKey: 'lB1', unit: 'cm', section: 'length', optional: true },
        { id: 'lB', label: 'lB', infoKey: 'lB', unit: 'cm', section: 'length', optional: true },
        { id: 'lA', label: 'lA', infoKey: 'lA', unit: 'cm', section: 'length', optional: true },
        { id: 'lZ', label: 'lZ', infoKey: 'lZ', unit: 'cm', section: 'length', optional: true },
        { id: 'sS', label: 'sS', infoKey: 'sS', unit: 'EU Size', section: 'length', optional: true },
    ]), [measurementType]);

    const madeToMeasureVendorTables = useMemo(
        () => vendorTables.filter((vendorTable) => vendorTable.madeToMeasure === true),
        [vendorTables]
    );

    const madeToMeasureVendors = useMemo(() => {
        const uniqueVendors = new Map();

        madeToMeasureVendorTables.forEach((vendorTable) => {
            if (!uniqueVendors.has(vendorTable.vendorName)) {
                uniqueVendors.set(vendorTable.vendorName, vendorTable);
            }
        });

        return Array.from(uniqueVendors.values()).sort((a, b) => a.vendorName.localeCompare(b.vendorName));
    }, [madeToMeasureVendorTables]);

    const selectedVendorProducts = useMemo(() => {
        if (!selectedVendorName) return [];

        const currentLengthType = measurements.lengthType || lengthType;
        const currentCompressionClass = measurements.compressionClass || compressionClass;
        const currentKnitting = measurements.knitting || knitting;
        const requiredBrei = currentKnitting ? KNITTING_TO_BREI[currentKnitting] : null;

        return madeToMeasureVendorTables
            .filter((vendorTable) => vendorTable.vendorName === selectedVendorName)
            .filter((vendorTable) => {
                const vendorLengthTypes = Array.isArray(vendorTable.lengthtypes) ? vendorTable.lengthtypes : [];
                const vendorClasses = Array.isArray(vendorTable.class) ? vendorTable.class.map(String) : [];
                const vendorBrei = Array.isArray(vendorTable.brei) ? vendorTable.brei : vendorTable.brei ? [vendorTable.brei] : [];

                if (currentLengthType && !vendorLengthTypes.includes(currentLengthType)) {
                    return false;
                }

                if (currentCompressionClass && !vendorClasses.includes(String(currentCompressionClass))) {
                    return false;
                }

                if (requiredBrei && !vendorBrei.includes(requiredBrei)) {
                    return false;
                }

                return true;
            })
            .sort((a, b) => (a.vendorType || a.vendorTableName || '').localeCompare(b.vendorType || b.vendorTableName || ''));
    }, [
        madeToMeasureVendorTables,
        selectedVendorName,
        measurements.lengthType,
        measurements.compressionClass,
        measurements.knitting,
        lengthType,
        compressionClass,
        knitting,
    ]);

    const getZmTension = (fieldId) => {
        const tensionFieldId = fieldId.replace(/[RL]$/, '');
        const defaultTension = DEFAULT_ZM_TENSION[tensionFieldId] || '';

        if (measurementType !== 'madeToMeasure') {
            return defaultTension;
        }

        const selectedVendorProduct = selectedVendorProducts.find(
            (vendorTable) => vendorTable.vendorTableID === selectedVendorProductId
        );

        return selectedVendorProduct?.measurements2Tension?.[tensionFieldId] || defaultTension;
    };

    useEffect(() => {
        const loadVendorTables = async () => {
            try {
                const response = await axios.get('/api/product-options/config');
                setVendorTables(response.data.vendorTables || []);
            } catch (error) {
                console.error('Error loading vendor tables:', error);
                setVendorTables([]);
            }
        };

        loadVendorTables();
    }, []);

    useEffect(() => {
        if (measurementType !== 'madeToMeasure') {
            setSelectedVendorName('');
            setSelectedVendorProductId('');
            setSelectedProduct(null);
            return;
        }

        if (!selectedVendorName || selectedVendorProducts.length === 0) {
            setSelectedVendorProductId('');
            setSelectedProduct(null);
            return;
        }

        if (!selectedVendorProducts.some((vendorTable) => vendorTable.vendorTableID === selectedVendorProductId)) {
            setSelectedVendorProductId('');
            setSelectedProduct(null);
        }
    }, [measurementType, selectedVendorName, selectedVendorProducts, selectedVendorProductId]);

    useEffect(() => {
        if (measurementType !== 'madeToMeasure') {
            return;
        }

        if (!selectedVendorName || selectedVendorProducts.length === 0) {
            return;
        }

        const selectedTable = selectedVendorProducts.find((vendorTable) => vendorTable.vendorTableID === selectedVendorProductId);

        if (!selectedTable) {
            setSelectedProduct(null);
            return;
        }

        setSelectedProduct({
            vendorId: selectedTable.vendorTableID,
            vendorName: selectedTable.vendorName,
            modelId: selectedTable.vendorTableID,
            modelName: selectedTable.vendorType || selectedTable.vendorTableName || selectedTable.vendorTableID,
            styleId: selectedTable.vendorTableID,
            styleName: selectedTable.vendorTableName || selectedTable.vendorType || selectedTable.vendorTableID,
            vendorTableID: selectedTable.vendorTableID,
            vendorType: selectedTable.vendorType,
            vendorTableName: selectedTable.vendorTableName,
            productType: 'madeToMeasure',
        });
    }, [measurementType, selectedVendorName, selectedVendorProductId, selectedVendorProducts]);

    useEffect(() => {
        if (measurementType !== 'madeToMeasure' || !selectedVendorName || selectedVendorProductId || !prefillVendorType) {
            return;
        }

        const normalizedPrefillVendorType = prefillVendorType.trim().toLowerCase();

        const matchedVendorProduct = selectedVendorProducts.find((vendorTable) => {
            const vendorType = (vendorTable.vendorType || '').trim().toLowerCase();
            const vendorTableName = (vendorTable.vendorTableName || '').trim().toLowerCase();
            const vendorTableID = (vendorTable.vendorTableID || '').trim().toLowerCase();

            return (
                vendorType === normalizedPrefillVendorType
                || vendorTableName === normalizedPrefillVendorType
                || vendorTableID === normalizedPrefillVendorType
            );
        });

        if (matchedVendorProduct) {
            setSelectedVendorProductId(matchedVendorProduct.vendorTableID);
            setPrefillVendorType('');
        }
    }, [measurementType, selectedVendorName, selectedVendorProductId, prefillVendorType, selectedVendorProducts]);

    // Load saved measurements from database if navigating back
    useEffect(() => {
        const loadMeasurements = async () => {
            const idToLoad = location.state?.measurementId;
            if (idToLoad) {
                try {
                    const response = await axios.get(`/api/measurements/${idToLoad}`);
                    const savedData = response.data;

                    // Always sync measurementId state from the authoritative DB response.
                    // This guards against stale useState initialisation in edge cases.
                    setMeasurementId(savedData._id);

                    // Always overwrite patient-identity fields from DB.
                    // This prevents stale sessionStorage data (written during a previous
                    // new-measurement flow) from contaminating the patient identity stored
                    // in this document when the user later saves.
                    setPatientId(savedData.patientId || '');
                    setPatientFirstName(savedData.patientFirstName || '');
                    setPatientLastName(savedData.patientLastName || '');

                    // Respect explicit navigation intent and lock ordered measurements.
                    // Do not force read-only solely because a product has been selected.
                    const explicitReadOnly = location.state?.isReadOnly === true;
                    const isMeasurementOrdered = savedData.ordered === true;
                    setIsReadOnly(explicitReadOnly || isMeasurementOrdered);

                    // Load ordered status for workflow timeline
                    if (savedData.ordered !== undefined) {
                        setIsOrdered(savedData.ordered);
                    }

                    // Load selected product for workflow timeline
                    if (savedData.selectedProduct) {
                        setSelectedProduct(savedData.selectedProduct);
                        setSelectedVendorName(savedData.selectedProduct.vendorName || '');
                        setSelectedVendorProductId(
                            savedData.selectedProduct.vendorTableID
                            || savedData.selectedProduct.modelId
                            || savedData.selectedProduct.styleId
                            || ''
                        );
                        setPrefillVendorType('');
                    } else if (savedData.selections?.vendor || savedData.selections?.vendortype) {
                        setSelectedVendorName(savedData.selections?.vendor || '');

                        if (savedData.selections?.vendorTableId) {
                            setSelectedVendorProductId(savedData.selections.vendorTableId);
                            setPrefillVendorType('');
                        } else {
                            setSelectedVendorProductId('');
                            setPrefillVendorType(savedData.selections?.vendortype || '');
                        }
                    }

                    if (savedData.measurements) {
                        // Ensure specification fields are included in measurements object
                        const updatedMeasurements = { ...savedData.measurements };
                        const resolvedNotes = normalizeNotes(savedData.notes);

                        if (savedData.compressionClass) {
                            updatedMeasurements.compressionClass = savedData.compressionClass;
                            setCompressionClass(savedData.compressionClass);
                        }
                        if (savedData.lengthType) {
                            updatedMeasurements.lengthType = savedData.lengthType;
                            setLengthType(savedData.lengthType);
                        }
                        if (savedData.knitting) {
                            updatedMeasurements.knitting = savedData.knitting;
                            setKnitting(savedData.knitting);
                        }
                        updatedMeasurements.notes = resolvedNotes;

                        // Replace the full patientDetails object from DB (not a partial merge)
                        setPatientDetails({
                            dateOfBirth: savedData.patientDateOfBirth || '',
                            gender: savedData.patientGender || '',
                            address: savedData.patientAddress || '',
                            phoneNumber: savedData.patientPhoneNumber || '',
                            email: savedData.patientEmail || '',
                            notes: resolvedNotes,
                            prescribingDoctor: savedData.prescribingDoctor || '',
                        });

                        setMeasurements(updatedMeasurements);
                    }
                    if (savedData.measurements2) {
                        setMeasurements2(savedData.measurements2);
                    }
                    if (savedData.order) {
                        setExistingOrder(savedData.order);
                        setOrderNote(savedData.order.orderNote || '');
                    }
                    if (savedData.measurementType) {
                        setMeasurementType(savedData.measurementType);
                    }
                    if (savedData.createdAt) {
                        setCreatedAt(savedData.createdAt);
                    }
                } catch (error) {
                    console.error('Error loading measurements:', error);
                }
            }
        };

        loadMeasurements();
    }, [location.state?.measurementId, location.state?.isReadOnly]);

    useEffect(() => {
        const allowedLengthTypes = measurementType === 'madeToMeasure' ? MADE_TO_MEASURE_LENGTH_OPTIONS : OFF_THE_SHELF_LENGTH_OPTIONS;
        const allowedCompressionClasses = measurementType === 'madeToMeasure' ? MADE_TO_MEASURE_COMPRESSION_OPTIONS : OFF_THE_SHELF_COMPRESSION_OPTIONS;

        setMeasurements((prev) => {
            const nextMeasurements = { ...prev };
            let hasChanges = false;

            if (nextMeasurements.lengthType && !allowedLengthTypes.includes(nextMeasurements.lengthType)) {
                delete nextMeasurements.lengthType;
                setLengthType('');
                hasChanges = true;
            }

            if (nextMeasurements.compressionClass && !allowedCompressionClasses.includes(nextMeasurements.compressionClass)) {
                delete nextMeasurements.compressionClass;
                setCompressionClass('');
                hasChanges = true;
            }

            return hasChanges ? nextMeasurements : prev;
        });
    }, [measurementType]);

    // Initialize speech recognition
    useEffect(() => {
        // Load patient info from sessionStorage — ONLY for new measurements.
        // When editing an existing measurement (measurementId in location.state),
        // patient info is loaded directly from the DB by the loadMeasurements effect
        // above. Using sessionStorage here would overwrite the authoritative DB data
        // with stale data from a previously opened patient, corrupting the document.
        const storedPatientInfo = !location.state?.measurementId
            ? sessionStorage.getItem('patientInfo')
            : null;
        if (storedPatientInfo) {
            const patientData = JSON.parse(storedPatientInfo);
            const initialNotes = normalizeNotes(patientData.notes);
            setPatientId(patientData.patientId || '');
            setPatientFirstName(patientData.firstName || '');
            setPatientLastName(patientData.lastName || '');
            setPatientDetails({
                dateOfBirth: patientData.dateOfBirth || '',
                gender: patientData.gender || '',
                address: patientData.address || '',
                phoneNumber: patientData.phoneNumber || '',
                email: patientData.email || '',
                notes: initialNotes,
                prescribingDoctor: patientData.prescribingDoctor || '',
            });
            setMeasurements((prev) => ({
                ...prev,
                notes: prev.notes ?? initialNotes,
            }));
            // Skip patient info questions since we have the data
            // Find first visible step
            let firstVisibleStepIndex = 0;
            while (firstVisibleStepIndex < measurementSteps.length && !isFieldVisible(measurementSteps[firstVisibleStepIndex].id)) {
                firstVisibleStepIndex++;
            }
            setCurrentStep(firstVisibleStepIndex);
        } else if (location.state?.patientId) {
            // If coming from editing an existing measurement
            const initialNotes = normalizeNotes(location.state.notes);
            setPatientId(location.state.patientId || '');
            setPatientFirstName(location.state.patientFirstName || '');
            setPatientLastName(location.state.patientLastName || '');
            setPatientDetails({
                dateOfBirth: location.state.patientDateOfBirth || '',
                gender: location.state.patientGender || '',
                address: location.state.patientAddress || '',
                phoneNumber: location.state.patientPhoneNumber || '',
                email: location.state.patientEmail || '',
                notes: initialNotes,
                prescribingDoctor: location.state.prescribingDoctor || '',
            });
            setMeasurements((prev) => ({
                ...prev,
                notes: prev.notes ?? initialNotes,
            }));
            // Find first visible step
            let firstVisibleStepIndex = 0;
            while (firstVisibleStepIndex < measurementSteps.length && !isFieldVisible(measurementSteps[firstVisibleStepIndex].id)) {
                firstVisibleStepIndex++;
            }
            setCurrentStep(firstVisibleStepIndex);
        } else {
            // If no patient info, start with patient info questions
            setCurrentStep(-2);
        }

        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'en-US';

            recognitionRef.current.onresult = (event) => {
                let finalTranscript = '';

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + ' ';
                    }
                }

                if (finalTranscript) {
                    processVoiceInput(finalTranscript.trim());
                }
            };

            recognitionRef.current.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);

                // Provide user-friendly error messages
                if (event.error === 'not-allowed') {
                    addToChatHistory('system',
                        'Microphone access denied. Please enable microphone permissions in your browser settings and ensure you are using HTTPS or localhost.'
                    );
                    alert('Microphone Permission Required\n\nTo use voice input:\n1. Enable microphone permissions for this site in Safari settings\n2. Make sure you are using HTTPS (secure connection)\n3. Tap the microphone button again to retry');
                } else if (event.error === 'no-speech') {
                    addToChatHistory('system', 'No speech detected. Please try again.');
                } else if (event.error === 'audio-capture') {
                    addToChatHistory('system', 'No microphone detected. Please connect a microphone and try again.');
                } else if (event.error === 'network') {
                    addToChatHistory('system', 'Network error. Please check your internet connection.');
                } else {
                    addToChatHistory('system', `Voice recognition error: ${event.error}. Please try manual input.`);
                }
            };

            recognitionRef.current.onend = () => {
                if (isListening) {
                    recognitionRef.current.start();
                }
            };
        } else {
            addToChatHistory('system', 'Voice recognition is not supported in your browser. Please use manual input.');
        }

        // Initial greeting - use ref to prevent duplicate in React Strict Mode
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;

            // For existing measurements use location.state for the name (sessionStorage
            // may contain stale data from a different patient).
            const isExisting = !!location.state?.measurementId;
            const storedInfo = !isExisting ? sessionStorage.getItem('patientInfo') : null;

            if (storedInfo) {
                const patientData = JSON.parse(storedInfo);
                // Find first visible step
                let firstVisibleStepIndex = 0;
                while (firstVisibleStepIndex < measurementSteps.length && !isFieldVisible(measurementSteps[firstVisibleStepIndex].id)) {
                    firstVisibleStepIndex++;
                }
                const firstStepLabel = firstVisibleStepIndex < measurementSteps.length ? measurementSteps[firstVisibleStepIndex].label : measurementSteps[0].label;
                addToChatHistory('assistant', `Hello! Let's measure ${patientData.firstName} ${patientData.lastName}. We'll start with ${firstStepLabel}. Please say the measurement value.`);
            } else if (isExisting && (location.state?.patientFirstName || location.state?.patientLastName)) {
                const firstName = location.state.patientFirstName || '';
                const lastName = location.state.patientLastName || '';
                // Find first visible step
                let firstVisibleStepIndex = 0;
                while (firstVisibleStepIndex < measurementSteps.length && !isFieldVisible(measurementSteps[firstVisibleStepIndex].id)) {
                    firstVisibleStepIndex++;
                }
                const firstStepLabel = firstVisibleStepIndex < measurementSteps.length ? measurementSteps[firstVisibleStepIndex].label : measurementSteps[0].label;
                addToChatHistory('assistant', `Hello! Let's measure ${firstName} ${lastName}. We'll start with ${firstStepLabel}. Please say the measurement value.`);
            } else {
                addToChatHistory('assistant', 'Hello! I\'ll guide you through measuring your patient. Let\'s start with patient information. What is the patient ID?');
            }
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    // Keep ref in sync with state
    useEffect(() => {
        currentStepRef.current = currentStep;
    }, [currentStep]);

    // Validate current step is visible when component state changes
    useEffect(() => {
        if (currentStep >= 0 && currentStep < measurementSteps.length) {
            const currentFieldId = measurementSteps[currentStep].id;
            if (!isFieldVisible(currentFieldId)) {
                // Current step is hidden, find next visible step
                let nextVisibleStepIndex = currentStep;
                while (nextVisibleStepIndex < measurementSteps.length && !isFieldVisible(measurementSteps[nextVisibleStepIndex].id)) {
                    nextVisibleStepIndex++;
                }
                if (nextVisibleStepIndex !== currentStep) {
                    setCurrentStep(nextVisibleStepIndex);
                }
            }
        }
    }, [currentStep, measurements.lengthType, measurements.compressionClass, measurements.knitting]);

    // When lengthType changes, ensure current step is visible
    // Note: measurements for hidden fields are intentionally preserved so that
    // switching back to a broader length type (e.g. AD → AG → AT) restores them.
    useEffect(() => {
        const currentLengthType = measurements.lengthType || lengthType;

        if (currentLengthType) {
            // Only adjust current step if it's actually hidden
            // For AT (all fields visible), this should never trigger
            if (currentStep >= 0 && currentStep < measurementSteps.length) {
                const currentFieldId = measurementSteps[currentStep].id;
                const isCurrentFieldVisible = isFieldVisible(currentFieldId);

                // Only change step if current field is actually hidden
                if (!isCurrentFieldVisible) {
                    // Current step is now hidden, find next visible step
                    const nextVisibleStep = findNextVisibleStep(currentStep - 1);
                    setCurrentStep(nextVisibleStep);
                }
            }
        }
    }, [measurements.lengthType, lengthType]);

    // Auto-scroll to current input field and open select dropdown if applicable
    useEffect(() => {
        if (currentStep >= 0 && currentStep < measurementSteps.length) {
            const currentFieldId = measurementSteps[currentStep].id;

            // Only scroll if the field is visible
            if (!isFieldVisible(currentFieldId)) {
                return;
            }

            const fieldElement = inputFieldRefs.current[currentFieldId];
            if (fieldElement) {
                fieldElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                // Auto-open dropdown if it's a select field
                const step = measurementSteps[currentStep];
                if (step.type === 'select') {
                    setTimeout(() => {
                        const selectElement = fieldElement.querySelector('select');
                        if (selectElement) {
                            selectElement.focus();
                            // Trigger dropdown opening
                            selectElement.click();
                        }
                    }, 300);
                }
            }
        }
    }, [currentStep, measurementSteps]);

    // Set initial focus to cA field when swapMeasuresOrder is enabled
    useEffect(() => {
        if (!swapMeasuresInitializedRef.current && swapMeasuresOrder && !location.state?.measurementId) {
            // When reversed order is enabled, still start with first visible step (specifications first)
            const firstVisibleStepIndex = findNextVisibleStep(-1);
            if (firstVisibleStepIndex >= 0) {
                setCurrentStep(firstVisibleStepIndex);
                swapMeasuresInitializedRef.current = true;
            }
        } else if (!swapMeasuresOrder && swapMeasuresInitializedRef.current) {
            // If swapMeasuresOrder is turned off, reset
            swapMeasuresInitializedRef.current = false;
        }
    }, [swapMeasuresOrder, measurementSteps, location.state?.measurementId]);

    // Handle keyboard navigation for reversed tab order
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only handle Tab key when swapMeasuresOrder is enabled
            if (!swapMeasuresOrder || e.key !== 'Tab') {
                return;
            }

            // Check if the active element is an input in our measurement fields
            const activeElement = document.activeElement;
            const isOurInput = activeElement &&
                (activeElement.tagName === 'INPUT' || activeElement.tagName === 'SELECT' || activeElement.tagName === 'TEXTAREA') &&
                Object.values(inputFieldRefs.current).some(ref => ref && ref.contains(activeElement));

            if (!isOurInput) {
                return;
            }

            e.preventDefault();

            // Find current field ID
            let currentFieldId = null;
            for (const [fieldId, ref] of Object.entries(inputFieldRefs.current)) {
                if (ref && ref.contains(activeElement)) {
                    currentFieldId = fieldId;
                    break;
                }
            }

            if (!currentFieldId) {
                return;
            }

            const currentIndex = measurementSteps.findIndex(step => step.id === currentFieldId);
            if (currentIndex === -1) {
                return;
            }

            const currentStep = measurementSteps[currentIndex];
            const isInSpecifications = currentStep.section === 'specifications';

            // Specifications always use normal order
            if (isInSpecifications) {
                // Normal Tab behavior for specifications
                const direction = e.shiftKey ? -1 : 1;
                let nextIndex = currentIndex + direction;

                // Find next/previous visible step
                while (nextIndex >= 0 && nextIndex < measurementSteps.length) {
                    const step = measurementSteps[nextIndex];

                    // If moving forward from specifications and we've left the specifications section,
                    // jump to the last visible measurement field
                    if (direction > 0 && step.section !== 'specifications') {
                        // Find the last visible measurement field
                        for (let i = measurementSteps.length - 1; i >= 0; i--) {
                            const lastStep = measurementSteps[i];
                            if (lastStep.section !== 'specifications' && isFieldVisible(lastStep.id)) {
                                nextIndex = i;
                                setCurrentStep(nextIndex);

                                // Focus the input field
                                setTimeout(() => {
                                    const nextFieldElement = inputFieldRefs.current[lastStep.id];
                                    if (nextFieldElement) {
                                        const input = nextFieldElement.querySelector('input, select, textarea');
                                        if (input) {
                                            input.focus();
                                        }
                                    }
                                }, 50);
                                break;
                            }
                        }
                        break;
                    }

                    if (isFieldVisible(step.id)) {
                        setCurrentStep(nextIndex);

                        // Focus the input field in the next step
                        setTimeout(() => {
                            const nextFieldElement = inputFieldRefs.current[step.id];
                            if (nextFieldElement) {
                                const input = nextFieldElement.querySelector('input, select, textarea');
                                if (input) {
                                    input.focus();
                                }
                            }
                        }, 50);
                        break;
                    }
                    nextIndex += direction;
                }
            } else {
                // Reverse the direction for measurement fields: Tab goes backwards, Shift+Tab goes forwards
                const direction = e.shiftKey ? 1 : -1;
                let nextIndex = currentIndex + direction;

                // Find next/previous visible step
                while (nextIndex >= 0 && nextIndex < measurementSteps.length) {
                    const step = measurementSteps[nextIndex];

                    // If moving backwards and we've reached specifications, stop at the last spec
                    if (direction > 0 && step.section === 'specifications') {
                        // Find the last specification field
                        for (let i = measurementSteps.length - 1; i >= 0; i--) {
                            const specStep = measurementSteps[i];
                            if (specStep.section === 'specifications' && isFieldVisible(specStep.id)) {
                                nextIndex = i;
                                break;
                            }
                        }
                        break;
                    }

                    if (isFieldVisible(step.id)) {
                        setCurrentStep(nextIndex);

                        // Focus the input field in the next step
                        setTimeout(() => {
                            const nextFieldElement = inputFieldRefs.current[step.id];
                            if (nextFieldElement) {
                                const input = nextFieldElement.querySelector('input, select, textarea');
                                if (input) {
                                    input.focus();
                                }
                            }
                        }, 50);
                        break;
                    }
                    nextIndex += direction;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [swapMeasuresOrder, measurementSteps]);

    const addToChatHistory = (type, message) => {
        if (type !== 'user') {
            setCurrentMessage({ message, stepIndex: currentStepRef.current });
        }
    };

    // Helper function to generate voice prompts with measurement descriptions
    const getStepPrompt = (step) => {
        if (step.type === 'select') {
            // For select fields, include options list
            const description = step.infoKey ? t(`legMeasuring.measurements.${step.infoKey}`, '') : '';
            const descriptionPart = description ? ` ${description}.` : '';
            return `${step.label}.${descriptionPart} Options are: ${step.options.join(', ')}.`;
        } else {
            // For measurement fields, include detailed description from translations
            const description = step.infoKey ? t(`legMeasuring.measurements.${step.infoKey}`, '') : '';
            return description ? `${step.label}. ${description}` : `${step.label}. Please provide the measurement.`;
        }
    };

    const processVoiceInput = (input) => {
        addToChatHistory('user', input);

        const stepIndex = currentStepRef.current;
        const inputLower = input.toLowerCase().trim();

        // Check for "go back" or "previous" command
        if ((inputLower.includes('go back') || inputLower === 'back' || inputLower === 'previous') && stepIndex >= 0) {
            // Find the previous visible step
            let previousStep = stepIndex - 1;
            while (previousStep >= 0 && !isFieldVisible(measurementSteps[previousStep].id)) {
                previousStep--;
            }

            if (previousStep >= 0) {
                setCurrentStep(previousStep);
                currentStepRef.current = previousStep;
                const step = measurementSteps[previousStep];
                const prompt = `Moved back to ${getStepPrompt(step)}`;
                addToChatHistory('assistant', prompt);
                return;
            } else {
                addToChatHistory('assistant', 'You are already at the beginning.');
                return;
            }
        }

        // Check for "next" command
        if (inputLower === 'next' && stepIndex >= 0) {
            const nextStepIndex = findNextVisibleStep(stepIndex);
            if (nextStepIndex < measurementSteps.length) {
                setCurrentStep(nextStepIndex);
                currentStepRef.current = nextStepIndex;
                const step = measurementSteps[nextStepIndex];
                const prompt = `Moved to ${getStepPrompt(step)}`;
                addToChatHistory('assistant', prompt);
                return;
            } else {
                addToChatHistory('assistant', 'All measurements complete! You can review and save now.');
                setCurrentStep(measurementSteps.length);
                currentStepRef.current = measurementSteps.length;
                return;
            }
        }

        // Check for navigation commands (go to, jump to, back to, etc.)
        const navigationKeywords = ['go to', 'jump to', 'back to', 'change', 'edit', 'correct', 'fix', 'update'];
        const isNavigationCommand = navigationKeywords.some(keyword => inputLower.includes(keyword));

        if (isNavigationCommand && stepIndex >= 0) {
            // Try to find the field the user wants to navigate to
            const targetStep = findStepByName(input);
            if (targetStep !== -1) {
                setCurrentStep(targetStep);
                currentStepRef.current = targetStep;
                const step = measurementSteps[targetStep];
                const prompt = `Moved to ${getStepPrompt(step)}`;
                addToChatHistory('assistant', prompt);
                return;
            } else {
                addToChatHistory('assistant', 'I couldn\'t find that field. Please try again or say the field name more clearly.');
                return;
            }
        }

        if (stepIndex === -2) {
            // Getting patient ID
            setPatientId(input);
            addToChatHistory('assistant', `Patient ID set to ${input}. What is the patient's name?`);
            setCurrentStep(-1);
            currentStepRef.current = -1;
        } else if (stepIndex === -1) {
            // Getting patient name - split into first and last name
            const nameParts = input.trim().split(/\s+/).filter(Boolean);
            if (nameParts.length === 1) {
                setPatientFirstName('');
                setPatientLastName(nameParts[0]);
            } else {
                setPatientLastName(nameParts[nameParts.length - 1]);
                setPatientFirstName(nameParts.slice(0, -1).join(' '));
            }
            // Find first visible step
            let firstVisibleStepIndex = 0;
            while (firstVisibleStepIndex < measurementSteps.length && !isFieldVisible(measurementSteps[firstVisibleStepIndex].id)) {
                firstVisibleStepIndex++;
            }
            if (firstVisibleStepIndex < measurementSteps.length) {
                const firstStep = measurementSteps[firstVisibleStepIndex];
                const firstStepPrompt = `Great! Now let's start. ${getStepPrompt(firstStep)}`;
                addToChatHistory('assistant', firstStepPrompt);
                setCurrentStep(firstVisibleStepIndex);
                currentStepRef.current = firstVisibleStepIndex;
            }
        } else if (stepIndex >= 0 && stepIndex < measurementSteps.length) {
            const step = measurementSteps[stepIndex];

            // Skip if this step is not visible
            if (!isFieldVisible(step.id)) {
                const nextVisibleStep = findNextVisibleStep(stepIndex);
                if (nextVisibleStep < measurementSteps.length) {
                    setCurrentStep(nextVisibleStep);
                    currentStepRef.current = nextVisibleStep;
                    const nextStep = measurementSteps[nextVisibleStep];
                    const nextPrompt = getStepPrompt(nextStep);
                    addToChatHistory('assistant', nextPrompt);
                }
                return;
            }

            // Handle select field - match spoken input to options
            if (step.type === 'select') {
                const inputLower = input.toLowerCase().trim();

                // Try to match the spoken input to one of the options
                const matchedOption = step.options.find(option => {
                    const optionLower = option.toLowerCase();
                    // Check for exact match or if input contains the full option (not vice versa)
                    // This prevents partial matches like 'a' matching 'AD'
                    return inputLower === optionLower || inputLower.includes(optionLower);
                });

                if (matchedOption) {
                    setMeasurements((prev) => ({
                        ...prev,
                        [step.id]: matchedOption,
                    }));

                    // Update individual state variables for specification fields
                    if (step.id === 'compressionClass') {
                        setCompressionClass(matchedOption);
                    } else if (step.id === 'lengthType') {
                        setLengthType(matchedOption);
                    } else if (step.id === 'knitting') {
                        setKnitting(matchedOption);
                    }

                    addToChatHistory('assistant', `Recorded: ${step.label} = ${matchedOption}`);

                    // Move to next visible step
                    // Pass the updated lengthType if this was a lengthType selection
                    const currentLengthTypeForNext = step.id === 'lengthType' ? matchedOption : null;
                    const nextStepIndex = findNextVisibleStep(stepIndex, currentLengthTypeForNext);
                    if (nextStepIndex < measurementSteps.length) {
                        const nextStep = measurementSteps[nextStepIndex];
                        setTimeout(() => {
                            const nextPrompt = `Next: ${getStepPrompt(nextStep)}`;
                            addToChatHistory('assistant', nextPrompt);
                            setCurrentStep(nextStepIndex);
                            currentStepRef.current = nextStepIndex;
                        }, 500);
                    } else {
                        setTimeout(() => {
                            addToChatHistory('assistant', 'All measurements complete! You can review and save now.');
                            setCurrentStep(measurementSteps.length);
                            currentStepRef.current = measurementSteps.length;
                        }, 500);
                    }
                } else {
                    addToChatHistory('assistant', `I didn't catch that. Please choose from: ${step.options.join(', ')}.`);
                }
            } else {
                // Handle number input for measurements
                const numbers = input.match(/\d+\.?\d*/g);

                if (numbers && numbers.length > 0) {
                    const value = parseFloat(numbers[0]);

                    setMeasurements((prev) => ({
                        ...prev,
                        [step.id]: value,
                    }));

                    addToChatHistory('assistant', `Recorded: ${step.label} = ${value} ${step.unit}`);

                    // Move to next visible step
                    // Use current lengthType state to ensure proper visibility checks
                    const nextStepIndex = findNextVisibleStep(stepIndex, measurements.lengthType || lengthType);
                    if (nextStepIndex < measurementSteps.length) {
                        const nextStep = measurementSteps[nextStepIndex];
                        setTimeout(() => {
                            const nextPrompt = `Next: ${getStepPrompt(nextStep)}`;
                            addToChatHistory('assistant', nextPrompt);
                            setCurrentStep(nextStepIndex);
                            currentStepRef.current = nextStepIndex;
                        }, 500);
                    } else {
                        setTimeout(() => {
                            addToChatHistory('assistant', 'All measurements complete! You can review and save now.');
                            setCurrentStep(measurementSteps.length);
                            currentStepRef.current = measurementSteps.length;
                        }, 500);
                    }
                } else {
                    addToChatHistory('assistant', 'I didn\'t catch a number. Please say the measurement value clearly.');
                }
            }
        }
    };

    const startVoiceRecognition = async () => {
        if (recognitionRef.current && !isListening) {
            try {
                // Check for microphone permissions on supported browsers
                if (navigator.permissions && navigator.permissions.query) {
                    try {
                        const permissionStatus = await navigator.permissions.query({ name: 'microphone' });

                        if (permissionStatus.state === 'denied') {
                            alert('Microphone Permission Denied\n\nPlease enable microphone access in your browser settings:\n\niOS/Safari:\n1. Go to Settings > Safari > Microphone\n2. Allow this website to access the microphone\n3. Refresh the page and try again\n\nNote: Voice input requires HTTPS or localhost.');
                            return;
                        }
                    } catch (permError) {
                        // Permission API not fully supported, continue anyway
                        console.log('Permission API not supported, attempting to start recognition anyway');
                    }
                }

                // Update state immediately for visual feedback on touch devices
                setIsListening(true);
                recognitionRef.current.start();
            } catch (error) {
                console.error('Error starting voice recognition:', error);
                setIsListening(false);
                addToChatHistory('system', 'Failed to start voice recognition. Please check microphone permissions.');
            }
        }
    };

    const stopVoiceRecognition = () => {
        if (recognitionRef.current && isListening) {
            // Update state immediately for visual feedback on touch devices
            setIsListening(false);
            recognitionRef.current.stop();
        }
    };

    const handleMeasurements2Input = (fieldId, value) => {
        if (isReadOnly) return;
        const parsedValue = parseFloat(value);
        const newM2 = { ...measurements2 };
        if (!value || value.trim() === '' || isNaN(parsedValue)) {
            delete newM2[fieldId];
        } else {
            newM2[fieldId] = parsedValue;
        }
        setMeasurements2(newM2);
    };

    const handleManualInput = (stepId, value) => {
        // Prevent changes in read-only mode
        if (isReadOnly) {
            return;
        }

        const step = measurementSteps.find(s => s.id === stepId);
        const newMeasurements = { ...measurements };

        // Handle select inputs (specifications)
        if (step && step.type === 'select') {
            if (!value || value.trim() === '') {
                delete newMeasurements[stepId];
            } else {
                newMeasurements[stepId] = value;
                // Update individual state variables for specification fields
                if (stepId === 'compressionClass') {
                    setCompressionClass(value);
                } else if (stepId === 'lengthType') {
                    setLengthType(value);
                } else if (stepId === 'knitting') {
                    setKnitting(value);
                }
            }
        } else if (step && step.type === 'textarea') {
            // Handle textarea inputs (notes)
            if (!value || value.trim() === '') {
                delete newMeasurements[stepId];
            } else {
                newMeasurements[stepId] = value;
                // Update patientDetails for notes field
                if (stepId === 'notes') {
                    setPatientDetails({ ...patientDetails, notes: value });
                }
            }
        } else {
            // Handle number inputs (measurements)
            const parsedValue = parseFloat(value);
            if (!value || value.trim() === '' || isNaN(parsedValue)) {
                delete newMeasurements[stepId];
            } else {
                newMeasurements[stepId] = parsedValue;
            }
        }

        setMeasurements(newMeasurements);

        // Check if all measurements are complete
        const allComplete = measurementSteps.every(step => {
            if (!isFieldVisible(step.id)) return true; // Skip hidden fields
            if (step.optional) return true; // Skip optional fields
            return step.type === 'select'
                ? (newMeasurements[step.id] !== undefined && newMeasurements[step.id] !== '')
                : (newMeasurements[step.id] !== undefined && newMeasurements[step.id] !== '' && !isNaN(newMeasurements[step.id]));
        });

        if (allComplete && currentStep < measurementSteps.length) {
            setTimeout(() => {
                addToChatHistory('assistant', 'All measurements complete! You can review and save now.');
                setCurrentStep(measurementSteps.length);
            }, 300);
        } else if (!allComplete && currentStep >= measurementSteps.length) {
            // If measurements are incomplete and we were showing the button, reset currentStep
            setCurrentStep(measurementSteps.length - 1);
        }
    };

    const handleSave = async () => {
        try {
            if (measurementType === 'madeToMeasure' && !selectedProduct) {
                addToChatHistory('system', 'Please select a vendor and vendor product before saving.');
                return;
            }

            const shouldSkipMatchingSearch = selectedProduct?.productType === 'madeToMeasure';

            let response;
            const resolvedNotes = measurements.notes ?? '';
            const measurementData = {
                userId: user?._id,
                organizationId: user?.organization,
                patientId,
                patientFirstName,
                patientLastName,
                patientDateOfBirth: patientDetails.dateOfBirth,
                patientGender: patientDetails.gender,
                patientAddress: patientDetails.address,
                patientPhoneNumber: patientDetails.phoneNumber,
                patientEmail: patientDetails.email,
                notes: resolvedNotes,
                prescribingDoctor: patientDetails.prescribingDoctor,
                measurements,
                measurements2: (measurementType === 'madeToMeasure' || isConditionalZmConfiguration()) ? measurements2 : undefined,
                compressionClass: measurements.compressionClass,
                lengthType: measurements.lengthType,
                knitting: measurements.knitting,
                measurementType: measurementType,
                selectedProduct: measurementType === 'madeToMeasure' ? selectedProduct : undefined,
                order: {
                    ...existingOrder,
                    orderNote,
                },
                completedSteps: Object.keys(measurements),
            };

            // If measurementId exists, update the existing measurement
            if (measurementId) {
                response = await axios.put(`/api/measurements/${measurementId}`, measurementData);
                addToChatHistory('assistant', 'Measurements updated successfully! Redirecting to product selection...');
            } else {
                // Otherwise, create a new measurement
                response = await axios.post('/api/measurements', measurementData);
                addToChatHistory('assistant', 'Measurements saved successfully! Redirecting to product selection...');
            }

            setTimeout(() => {
                navigate('/stocking-selection', {
                    state: {
                        measurements,
                        measurements2,
                        matchingProducts: shouldSkipMatchingSearch ? [] : (response.data.matchingProducts || []),
                        compressionClass: measurements.compressionClass,
                        lengthType: measurements.lengthType,
                        knitting: measurements.knitting,
                        measurementType: measurementType,
                        selectedProduct: measurementType === 'madeToMeasure' ? selectedProduct : undefined,
                        patientId,
                        patientFirstName,
                        patientLastName,
                        notes: response.data.notes ?? resolvedNotes,
                        orderNote: response.data.order?.orderNote ?? orderNote,
                        measurementId: response.data._id,
                        isReadOnly: isReadOnly,
                        ordered: isOrdered,
                    },
                });
            }, 1500);
        } catch (error) {
            console.error('Error saving measurements:', error);
            addToChatHistory('system', 'Error saving measurements. Please try again.');
        }
    };

    const getCompletedCount = (section) => {
        return measurementSteps.filter(
            (step) => step.section === section && isFieldVisible(step.id) && measurements[step.id] !== undefined
        ).length;
    };

    const getTotalCount = (section) => {
        return measurementSteps.filter((step) => step.section === section && isFieldVisible(step.id)).length;
    };

    const areAllMeasurementsComplete = () => {
        // Check if specifications are complete (required, excluding optional fields)
        const specificationsComplete = measurementSteps
            .filter(step => step.section === 'specifications' && !step.optional)
            .every(step => {
                const value = measurements[step.id];
                return value !== undefined && value !== '';
            });

        if (!specificationsComplete) return false;

        if (measurementType === 'madeToMeasure' && !selectedProduct) {
            return false;
        }

        // Check length measurement
        const lengthMeasurementsComplete = measurementSteps
            .filter(step => step.section === 'length' && isFieldVisible(step.id))
            .every(step => {
                const value = measurements[step.id];
                let isRequired = false;
                if (step.id === 'lG') {
                    // lG is required for AG and AT
                    isRequired = measurements.lengthType === 'AG' || measurements.lengthType === 'AT';
                } else if (step.id === 'lD') {
                    // lD is required for AD
                    isRequired = measurements.lengthType === 'AD';
                }
                return !isRequired || (value !== undefined && value !== '' && !isNaN(value));
            });

        if (!lengthMeasurementsComplete) return false;

        // Hip measurements are optional - no check needed

        // Check if right leg measurements are complete
        const rightLegSteps = measurementSteps.filter(step =>
            (step.section === 'right' || step.section === 'rightFoot') &&
            isFieldVisible(step.id) &&
            !step.optional
        );
        const rightLegMeasurementsComplete = rightLegSteps.length > 0 && rightLegSteps.every(step => {
            const value = measurements[step.id];
            return value !== undefined && value !== '' && !isNaN(value);
        });
        const rightZmComplete = !isConditionalZmConfiguration() || REQUIRED_CONDITIONAL_ZM_FIELDS.right.every(fieldId => {
            const value = measurements2[fieldId];
            return value !== undefined && value !== '' && !isNaN(value);
        });
        const rightLegComplete = rightLegMeasurementsComplete && rightZmComplete;

        // Check if left leg measurements are complete
        const leftLegSteps = measurementSteps.filter(step =>
            (step.section === 'left' || step.section === 'leftFoot') &&
            isFieldVisible(step.id) &&
            !step.optional
        );
        const leftLegMeasurementsComplete = leftLegSteps.length > 0 && leftLegSteps.every(step => {
            const value = measurements[step.id];
            return value !== undefined && value !== '' && !isNaN(value);
        });
        const leftZmComplete = !isConditionalZmConfiguration() || REQUIRED_CONDITIONAL_ZM_FIELDS.left.every(fieldId => {
            const value = measurements2[fieldId];
            return value !== undefined && value !== '' && !isNaN(value);
        });
        const leftLegComplete = leftLegMeasurementsComplete && leftZmComplete;

        // At least one leg must be complete (either right OR left)
        return rightLegComplete || leftLegComplete;
    };

    // Helper function to determine if a field should be visible based on lengthType
    const isFieldVisible = (fieldId, providedLengthType = null) => {
        const currentLengthType = providedLengthType || measurements.lengthType || lengthType;

        // Always show specification fields
        if (fieldId === 'compressionClass' || fieldId === 'lengthType' || fieldId === 'knitting') {
            return true;
        }

        // Fields that contain cE, cF, cG (thigh, above/below knee related)
        const cEFields = ['cER', 'cEL', 'lE'];
        const cFFields = ['cFR', 'cFL', 'lF'];
        const cGFields = ['cGR', 'cGL', 'lG'];

        // Fields that contain cH, cT (hip/waist related)
        const cHFields = ['cH', 'lH'];
        const cTFields = ['cT', 'lT'];

        // Base fields always shown: cB, cB1, cC, cD
        const baseFields = [
            'cBR', 'cB1R', 'cCR', 'cDR',
            'cBL', 'cB1L', 'cCL', 'cDL',
            // Foot measurements
            'lAR', 'cYR',
            'lAL', 'cYL',
            // Length measurements
            'lD', 'lC', 'lB1', 'lB', 'cA', 'lZ', 'sS'
        ];

        if (currentLengthType === 'AD') {
            // AD: Show only cB, cB1, cC, cD (hide cE, cF, cG, cH, cT)
            if (baseFields.includes(fieldId)) return true;
            if (cEFields.includes(fieldId) || cFFields.includes(fieldId) || cGFields.includes(fieldId)) return false;
            if (cHFields.includes(fieldId) || cTFields.includes(fieldId)) return false;
        } else if (currentLengthType === 'AG') {
            // AG: Show cB, cB1, cC, cD, cE, cF, cG (hide cH, cT)
            if (baseFields.includes(fieldId)) return true;
            if (cEFields.includes(fieldId) || cFFields.includes(fieldId) || cGFields.includes(fieldId)) return true;
            if (cHFields.includes(fieldId) || cTFields.includes(fieldId)) return false;
        } else if (currentLengthType === 'AT') {
            // AT: Show all fields
            return true;
        }

        // Default: show all if lengthType not selected yet
        return true;
    };

    // Helper function to find the next visible step
    const findNextVisibleStep = (currentIndex, providedLengthType = null) => {
        for (let i = currentIndex + 1; i < measurementSteps.length; i++) {
            if (isFieldVisible(measurementSteps[i].id, providedLengthType)) {
                return i;
            }
        }
        return measurementSteps.length; // All complete
    };

    // Helper function to find step by field name/label for voice navigation
    const findStepByName = (input) => {
        const inputLower = input.toLowerCase().trim();

        // Detect left/right preference in input
        const hasLeft = inputLower.includes('left');
        const hasRight = inputLower.includes('right');
        const sideSpecified = hasLeft || hasRight;
        const preferredSide = hasLeft ? 'left' : hasRight ? 'right' : null;

        // Try to match common field names/abbreviations
        const fieldMappings = {
            'cc': ['cCR', 'cCL'],  // Ankle Circumference
            'cd': ['cDR', 'cDL'],  // Calf Circumference
            'ce': ['cER', 'cEL'],  // Below Knee Circumference
            'cf': ['cFR', 'cFL'],  // Above Knee Circumference
            'cg': ['cGR', 'cGL'],  // Thigh Circumference
            'cb': ['cBR', 'cBL'],  // Ankle to Knee Circumference
            'cb1': ['cB1R', 'cB1L'],  // Knee to Groin Circumference
            'cy': ['cYR', 'cYL'],  // Heel Circumference
            'ca': ['cAR', 'cAL'],  // Instep Circumference
            'la': ['lAR', 'lAL'],  // Foot Length
            'lz': ['lZR', 'lZL'],  // Foot Width
            'ss': ['sSR', 'sSL'],  // Shoe Size
            'ct': ['cT'],  // Hip Circumference
            'ch': ['cH'],  // Waist Circumference
            'ankle': ['cCR', 'cCL'],  // Ankle
            'calf': ['cDR', 'cDL'],  // Calf
            'knee': ['cER', 'cEL'],  // Knee
            'thigh': ['cGR', 'cGL'],  // Thigh
            'class': ['compressionClass'],
            'compression': ['compressionClass'],
            'length': ['lengthType'],
            'knitting': ['knitting']
        };

        // Helper to check if field matches the preferred side
        const matchesSide = (fieldId) => {
            if (!sideSpecified) return true; // No preference
            // Check if field ends with L (left) or R (right)
            if (preferredSide === 'left') return fieldId.endsWith('L');
            if (preferredSide === 'right') return fieldId.endsWith('R');
            return true;
        };

        for (let i = 0; i < measurementSteps.length; i++) {
            const step = measurementSteps[i];
            const labelLower = step.label.toLowerCase();

            // Direct label match with side consideration
            if ((labelLower.includes(inputLower) || inputLower.includes(labelLower.split(':')[0].trim()))
                && matchesSide(step.id) && isFieldVisible(step.id)) {
                return i;
            }

            // Check mappings with side preference
            for (const [key, value] of Object.entries(fieldMappings)) {
                if (inputLower.includes(key)) {
                    if (Array.isArray(value)) {
                        // Filter by side preference if specified
                        const matchingFields = sideSpecified
                            ? value.filter(fieldId => matchesSide(fieldId))
                            : value;

                        if (matchingFields.includes(step.id) && isFieldVisible(step.id)) {
                            return i;
                        }
                    }
                }
            }
        }

        return -1; // Not found
    };

    // Helper function to get short label (before colon) for mobile view
    const getShortLabel = (label) => {
        const colonIndex = label.indexOf(':');
        return colonIndex > 0 ? label.substring(0, colonIndex) : label;
    };

    // Helper function to determine if a field is required (mandatory) based on lengthType
    const isFieldRequired = (fieldId) => {
        const ltValue = lengthType || measurements.lengthType;

        // Specifications section - all required except notes
        if (['lengthType', 'compressionClass', 'knitting'].includes(fieldId)) {
            return true;
        }
        if (fieldId === 'notes') {
            return false;
        }

        // Hip measurements - always optional
        if (['cT', 'cH'].includes(fieldId)) {
            return false;
        }

        // Leg measurements - all required except cB1
        if (['cGR', 'cFR', 'cER', 'cDR', 'cCR', 'cBR', 'cGL', 'cFL', 'cEL', 'cDL', 'cCL', 'cBL'].includes(fieldId)) {
            return true;
        }

        // cB1 is optional
        if (['cB1R', 'cB1L'].includes(fieldId)) {
            return false;
        }

        // Foot measurements - all optional
        if (['cYR', 'cAR', 'cYL', 'cAL'].includes(fieldId)) {
            return false;
        }

        // Length measurements - mandatory based on lengthType:
        // AD => lD is mandatory
        if (fieldId === 'lD') {
            return ltValue === 'AD';
        }
        // AG => lG is mandatory
        // AT => lG is mandatory
        if (fieldId === 'lG') {
            return ltValue === 'AG' || ltValue === 'AT';
        }

        // All other length measurements and sS are optional
        if (fieldId.startsWith('l') || fieldId === 'sS') {
            return false;
        }

        return false;
    };

    // Handler for field focus - sets current step when user clicks on a field
    const handleFieldFocus = (stepId) => {
        const stepIndex = measurementSteps.findIndex(s => s.id === stepId);
        if (stepIndex !== -1 && isFieldVisible(stepId)) {
            setCurrentStep(stepIndex);
            const step = measurementSteps[stepIndex];
            const prompt = step.type === 'textarea'
                ? `${step.label}. You can add any additional notes here.`
                : `Now at ${getStepPrompt(step)}`;
            addToChatHistory('assistant', prompt);
            // Highlight corresponding measurement on SVG diagram
            const legMeasureMap = {
                cGR: 'cG', cGL: 'cG',
                cFR: 'cF', cFL: 'cF',
                cER: 'cE', cEL: 'cE',
                cDR: 'cD', cDL: 'cD',
                cCR: 'cC', cCL: 'cC',
                cB1R: 'cB1', cB1L: 'cB1',
                cBR: 'cB', cBL: 'cB',
                cYR: 'cY', cYL: 'cY',
                cAR: 'cA', cAL: 'cA',
            };
            const lengthMeasureMap = {
                lT: 'lT', lH: 'lH', lG: 'lG', lF: 'lF', lE: 'lE',
                lD: 'lD', lC: 'lC', lB1: 'lB1', lB: 'lB', lA: 'lA',
                lZ: 'lZ', sS: 'sS',
            };
            setActiveLegMeasure(legMeasureMap[stepId] || null);
            setActiveLengthMeasure(lengthMeasureMap[stepId] || null);
        }
    };

    // Navigation handlers for moving between pages
    const handleNavigateToPatientInfo = () => {
        // Determine completed steps based on existing data
        const steps = [];
        console.log('LegMeasuring - Navigation data:', {
            hasMeasurements: measurements && Object.keys(measurements).length > 0,
            hasSelectedProduct: !!selectedProduct,
            isOrdered: isOrdered,
            measurements: measurements,
            selectedProduct: selectedProduct
        });
        if (measurements && Object.keys(measurements).length > 0) {
            steps.push('measurements');
        }
        if (selectedProduct) {
            steps.push('productSelection');
        }
        if (isOrdered) {
            steps.push('order');
        }
        console.log('LegMeasuring - completedSteps:', steps);

        navigate('/patient-info', {
            state: {
                measurementId: measurementId,
                patientId: patientId,
                patientFirstName: patientFirstName,
                patientLastName: patientLastName,
                dateOfBirth: patientDetails.dateOfBirth,
                gender: patientDetails.gender,
                address: patientDetails.address,
                phoneNumber: patientDetails.phoneNumber,
                email: patientDetails.email,
                notes: measurements.notes ?? '',
                prescribingDoctor: patientDetails.prescribingDoctor,
                measurements: measurements,
                measurements2: measurements2,
                compressionClass: compressionClass,
                lengthType: lengthType,
                knitting: knitting,
                measurementType: measurementType,
                isReadOnly: isReadOnly,
                allowPatientEdit: location.state?.allowPatientEdit === true,
                allowNotesEdit: location.state?.allowNotesEdit === true,
                completedSteps: steps,
                ordered: isOrdered,
                selectedProduct: selectedProduct,
            }
        });
    };

    const handleNavigateToStockingSelection = () => {
        navigate('/stocking-selection', {
            state: {
                measurementId: measurementId,
                patientId: patientId,
                patientFirstName: patientFirstName,
                patientLastName: patientLastName,
                notes: measurements.notes ?? '',
                measurements: measurements,
                measurements2: measurements2,
                compressionClass: compressionClass,
                lengthType: lengthType,
                knitting: knitting,
                measurementType: measurementType,
                selectedProduct: selectedProduct,
                matchingProducts: location.state?.matchingProducts,
                orderNote: orderNote,
                isReadOnly: isReadOnly,
                ordered: isOrdered,
            }
        });
    };

    const handleSwapLegsToggle = async () => {
        const newSwapValue = !swapLegSides;
        setSwapLegSides(newSwapValue);

        // Save preference to database
        await updateUserPreferences({ swapLegSides: newSwapValue });
    };

    const handleSwapMeasuresOrderToggle = async () => {
        const newSwapValue = !swapMeasuresOrder;
        setSwapMeasuresOrder(newSwapValue);

        // Save preference to database
        await updateUserPreferences({ swapMeasuresOrder: newSwapValue });

        // Always reset to first visible step (specifications remain first)
        const firstVisibleStepIndex = findNextVisibleStep(-1);
        if (firstVisibleStepIndex >= 0) {
            setCurrentStep(firstVisibleStepIndex);
        }
    };

    // SVG leg outline color: blue for right leg, purple for left leg, dark for everything else
    const activeSection = measurementSteps[currentStep]?.section;
    const guidanceColorClasses = {
        right: 'border-blue-600 bg-blue-50 text-blue-950',
        rightFoot: 'border-blue-600 bg-blue-50 text-blue-950',
        left: 'border-purple-600 bg-purple-50 text-purple-950',
        leftFoot: 'border-purple-600 bg-purple-50 text-purple-950',
        specifications: 'border-purple-600 bg-purple-50 text-purple-950',
        length: 'border-green-600 bg-green-50 text-green-950',
        hip: 'border-orange-600 bg-orange-50 text-orange-950',
    }[activeSection] || 'border-gray-500 bg-gray-50 text-gray-900';
    const legOutlineColor = (activeSection === 'right' || activeSection === 'rightFoot')
        ? '#3498db'
        : (activeSection === 'left' || activeSection === 'leftFoot')
            ? '#7c3aed'
            : '#333';
    // SVG lengths outline color: green when length fields are active, black otherwise
    const lengthOutlineColor = activeSection === 'length' ? '#16a34a' : '#333';

    return (
        <div className="page-transition h-full bg-gray-50">
            <div className="h-full flex flex-col">
                {/* Header */}
                <div className="bg-white shadow-sm px-6 py-4">
                    {/* Progress Indicator */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between text-sm max-w-2xl mx-auto">
                            <div className="flex items-center text-green-600 font-semibold">
                                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center mr-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <span>{t('patientInfo.progress.step1')}</span>
                            </div>
                            <div className="flex-1 h-1 bg-green-600 mx-4"></div>
                            <div className="flex items-center text-blue-600 font-semibold">
                                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2">2</div>
                                <span>{t('patientInfo.progress.step2')}</span>
                            </div>
                            <div className={`flex-1 h-1 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>
                            <div className={`flex items-center ${isOrdered ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                                <div className={`w-8 h-8 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} ${isOrdered ? 'text-white' : 'text-gray-600'} rounded-full flex items-center justify-center mr-2`}>
                                    {isOrdered ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        '3'
                                    )}
                                </div>
                                <span>{t('patientInfo.progress.step3')}</span>
                            </div>
                            <div className={`flex-1 h-1 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} mx-4`}></div>
                            <div className={`flex items-center ${isOrdered ? 'text-green-600 font-semibold' : 'text-gray-400'}`}>
                                <div className={`w-8 h-8 ${isOrdered ? 'bg-green-600' : 'bg-gray-200'} ${isOrdered ? 'text-white' : 'text-gray-600'} rounded-full flex items-center justify-center mr-2`}>
                                    {isOrdered ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        '4'
                                    )}
                                </div>
                                <span>{t('patientInfo.progress.step4')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Read-only mode indicator */}
                    {isReadOnly && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded px-4 py-2 mb-4">
                            <div className="flex items-center">
                                <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-yellow-800 font-semibold">
                                    {t('legMeasuringPage.readOnlyWarning')}
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold">{t('legMeasuringPage.stockingSpecifications')}</h1>
                            {(patientFirstName || patientLastName) && (
                                <>
                                    <p className="text-sm text-gray-600">
                                        {t('measurements.stockingSelection.patientLabel')}: {formatPatientNameForDisplay(patientFirstName, patientLastName)} (ID: {patientId})
                                    </p>
                                    {measurementType && (
                                        <p className="text-sm text-gray-600">
                                            <span className="font-semibold">{t(`measurements.legMeasuring.measurementType.${measurementType}`)}</span>
                                        </p>
                                    )}
                                    {isReadOnly && createdAt && (
                                        <p className="text-sm text-gray-600">
                                            {t('measurements.stockingSelection.measurementDate')}: {(() => {
                                                const date = new Date(createdAt);
                                                const day = String(date.getDate()).padStart(2, '0');
                                                const month = date.toLocaleString('en', { month: 'short' });
                                                const year = date.getFullYear();
                                                return `${day}-${month}-${year}`;
                                            })()}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                        <button
                            onClick={() => navigate('/patients')}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                    {/* Left Side - Visual Diagram - Mobile: show only input fields */}
                    <div className="w-full md:w-2/3 h-auto md:h-full bg-white md:border-r overflow-y-auto p-3 md:p-6 md:resize-x">

                        {/* Specifications - Top Section */}
                        <div className="mb-8 pb-8 border-b-2 border-gray-300">
                            <h3 className="text-lg font-semibold mb-4 text-purple-600">
                                {t('legMeasuringPage.productSpecifications')} ({getCompletedCount('specifications')}/{getTotalCount('specifications')})
                            </h3>

                            <div className="flex flex-col gap-3 md:grid md:grid-cols-2 md:gap-4 lg:grid-cols-3 lg:gap-6">
                                {/* Measurement Type Selection - PRO Feature */}
                                {canShowMadeToMeasureSwitch && !isReadOnly && (
                                    <div className="md:col-span-2 lg:col-span-3 p-4 rounded-lg border-2 border-purple-200 bg-purple-50">
                                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                                            {t('measurements.legMeasuring.measurementType.label')}
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-4">
                                            <label className="flex items-center cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="measurementType"
                                                    value="offTheShelf"
                                                    checked={measurementType === 'offTheShelf'}
                                                    onChange={(e) => setMeasurementType(e.target.value)}
                                                    className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                                />
                                                <span className="ml-2 text-gray-700">
                                                    {t('measurements.legMeasuring.measurementType.offTheShelf')}
                                                </span>
                                            </label>
                                            <label className="flex items-center cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="measurementType"
                                                    value="madeToMeasure"
                                                    checked={measurementType === 'madeToMeasure'}
                                                    onChange={(e) => setMeasurementType(e.target.value)}
                                                    className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                                                />
                                                <span className="ml-2 text-gray-700">
                                                    {t('measurements.legMeasuring.measurementType.madeToMeasure')}
                                                </span>
                                            </label>
                                        </div>
                                        <p className="text-xs text-gray-600 mt-2">
                                            {measurementType === 'offTheShelf'
                                                ? t('measurements.legMeasuring.measurementType.offTheShelfDesc')
                                                : t('measurements.legMeasuring.measurementType.madeToMeasureDesc')
                                            }
                                        </p>
                                    </div>
                                )}

                                {measurementSteps
                                    .filter((step) => step.section === 'specifications' && isFieldVisible(step.id) && step.type !== 'textarea')
                                    .map((step) => (
                                        <div
                                            key={step.id}
                                            ref={(el) => inputFieldRefs.current[step.id] = el}
                                            className={`p-3 rounded-lg border ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-400'
                                                : measurements[step.id]
                                                    ? 'bg-green-50 border-green-300'
                                                    : 'bg-gray-50 border-gray-200'
                                                }`}
                                        >
                                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-2">
                                                <span className="font-medium flex items-center">
                                                    <span className="md:hidden">{getShortLabel(t(step.label))} {!step.optional && <span className="text-red-500">*</span>}</span>
                                                    <span className="hidden md:inline">{t(step.label)} {!step.optional && <span className="text-red-500">*</span>}</span>
                                                    <MeasurementInfo infoKey={step.infoKey} />
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <select
                                                        value={measurements[step.id] || ''}
                                                        onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                        onFocus={() => handleFieldFocus(step.id)}
                                                        disabled={isReadOnly}
                                                        className={`w-full md:w-full lg:w-28 px-2 py-0.5 text-sm border rounded transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                            ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-400 shadow-lg'
                                                            : measurements[step.id]
                                                                ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                : 'border-gray-300'
                                                            } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                    >
                                                        <option value="">{t('legMeasuringPage.selectPlaceholder')}</option>
                                                        {step.options && step.options.map(option => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                    {measurements[step.id] && (
                                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                {/* Notes Fields - 2 column layout */}
                                {measurementSteps
                                    .filter((step) => step.section === 'specifications' && isFieldVisible(step.id) && step.type === 'textarea')
                                    .map((step) => (
                                        <div
                                            key={step.id}
                                            ref={(el) => inputFieldRefs.current[step.id] = el}
                                            className="md:col-span-2 lg:col-span-3 p-3 rounded-lg border bg-gray-50 border-gray-200"
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {/* Internal Notes */}
                                                <div className={`p-2 rounded-lg border ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-400'
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-white border-gray-200'
                                                    }`}>
                                                    <label className="block font-medium mb-2 text-gray-700">
                                                        {t('patientInfo.fields.notes')}
                                                    </label>
                                                    <textarea
                                                        value={measurements[step.id] || ''}
                                                        onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                        onFocus={() => handleFieldFocus(step.id)}
                                                        disabled={isReadOnly}
                                                        placeholder={t('legMeasuringPage.notesPlaceholder')}
                                                        rows="4"
                                                        className={`w-full px-3 py-2 border rounded-lg transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                            ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-400 shadow-lg'
                                                            : measurements[step.id]
                                                                ? 'bg-white border-green-500 text-green-700'
                                                                : 'border-gray-300'
                                                            } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                    />
                                                    {measurements[step.id] && (
                                                        <div className="flex items-center mt-2">
                                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                                {/* Supplier Order Note */}
                                                <div className={`p-2 rounded-lg border ${orderNote
                                                    ? 'bg-green-50 border-green-300'
                                                    : 'bg-white border-gray-200'
                                                    }`}>
                                                    <label className="block font-medium mb-2 text-gray-700">
                                                        {t('measurements.stockingSelection.order.orderNote')}
                                                    </label>
                                                    <textarea
                                                        value={orderNote}
                                                        onChange={(e) => setOrderNote(e.target.value)}
                                                        disabled={isReadOnly}
                                                        placeholder={t('measurements.stockingSelection.order.orderNotePlaceholder') || 'Add notes for this order'}
                                                        rows="4"
                                                        className={`w-full px-3 py-2 border rounded-lg transition-all ${orderNote
                                                            ? 'bg-white border-green-500 text-green-700'
                                                            : 'border-gray-300'
                                                            } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                    />
                                                    {orderNote && (
                                                        <div className="flex items-center mt-2">
                                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                {measurementType === 'madeToMeasure' && (
                                    <div className="md:col-span-2 lg:col-span-3 p-4 rounded-lg border-2 border-indigo-200 bg-indigo-50">
                                        <h4 className="text-sm font-semibold text-indigo-700 mb-3">
                                            Select Vendor Product
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Vendor
                                                </label>
                                                <select
                                                    value={selectedVendorName}
                                                    onChange={(e) => {
                                                        setSelectedVendorName(e.target.value);
                                                        setSelectedVendorProductId('');
                                                        setPrefillVendorType('');
                                                        setSelectedProduct(null);
                                                    }}
                                                    disabled={isReadOnly}
                                                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-all ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 bg-white'}`}
                                                >
                                                    <option value="">Select vendor</option>
                                                    {madeToMeasureVendors.map((vendorTable) => (
                                                        <option key={vendorTable.vendorName} value={vendorTable.vendorName}>
                                                            {vendorTable.vendorName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Vendor Product
                                                </label>
                                                <select
                                                    value={selectedVendorProductId}
                                                    onChange={(e) => setSelectedVendorProductId(e.target.value)}
                                                    disabled={isReadOnly || !selectedVendorName}
                                                    className={`w-full px-3 py-2 border rounded-lg text-sm transition-all ${isReadOnly || !selectedVendorName ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 bg-white'}`}
                                                >
                                                    <option value="">
                                                        {selectedVendorName ? 'Select vendor product' : 'Select a vendor first'}
                                                    </option>
                                                    {selectedVendorProducts.map((vendorTable) => (
                                                        <option key={vendorTable.vendorTableID} value={vendorTable.vendorTableID}>
                                                            {vendorTable.vendorType || vendorTable.vendorTableName || vendorTable.vendorTableID}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {selectedProduct && (
                                            <p className="mt-3 text-xs text-indigo-700">
                                                Selected: {selectedProduct.vendorName} / {selectedProduct.modelName}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Hip Measurements - Top Section - Only show for AT (All-Thigh) and AG (Above-Knee) length types */}
                        {(measurements.lengthType === 'AT' || lengthType === 'AT' || measurements.lengthType === 'AG' || lengthType === 'AG') && (
                            <div className="mb-8 pb-8 border-b-2 border-gray-300">
                                <h3 className="text-lg font-semibold mb-4 text-orange-600">
                                    {t('legMeasuringPage.hipWaist')} ({getCompletedCount('hip')}/{getTotalCount('hip')})
                                </h3>

                                {/* Hip & Waist Diagram 
                                <div className="flex justify-center mb-4">
                                    <img
                                        src={require('../images/leg_circ_length_foot_orange_cropped.png')}
                                        alt="Hip and Waist Measurement Diagram"
                                        className="border rounded-lg bg-gray-50 cursor-pointer hover:opacity-80 transition-opacity"
                                        style={{ maxWidth: '200px', height: 'auto' }}
                                        onClick={() => setShowImageModal(true)}
                                        title="Click to enlarge"
                                    />
                                </div>
                                */}

                                <div className="space-y-2">
                                    {measurementSteps
                                        .filter((step) => step.section === 'hip' && isFieldVisible(step.id))
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                ref={(el) => inputFieldRefs.current[step.id] = el}
                                                className={`p-3 rounded-lg border ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? 'bg-orange-50 border-orange-300 ring-2 ring-orange-400'
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                                                    <span className="font-medium flex items-center">
                                                        <span className="md:hidden">{getShortLabel(step.label)} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <span className="hidden md:inline">{step.label} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <MeasurementInfo infoKey={step.infoKey} />
                                                    </span>
                                                    <div className="grid grid-cols-[5rem_5rem_auto] items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            {measurementType === 'madeToMeasure' && (
                                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">HM</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                placeholder={step.unit}
                                                                value={measurements[step.id] || ''}
                                                                className={`w-20 px-2 py-1 border rounded transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                                    ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-400 shadow-lg'
                                                                    : measurements[step.id]
                                                                        ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                        : 'border-gray-300'
                                                                    } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                                onFocus={() => handleFieldFocus(step.id)}
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>
                                                        {shouldShowZmInputs(step.id) && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">ZM{getZmTension(step.id) && ` ${getZmTension(step.id)}`}{isConditionalZmRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    placeholder={step.unit}
                                                                    value={measurements2[step.id] || ''}
                                                                    className={`w-20 px-2 py-1 border rounded transition-all ${measurements2[step.id]
                                                                        ? 'bg-white border-purple-400 text-purple-700 font-semibold'
                                                                        : 'border-purple-200'
                                                                        } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                    onChange={(e) => handleMeasurements2Input(step.id, e.target.value)}
                                                                    onFocus={() => handleFieldFocus(step.id)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                        <span className="text-gray-600 text-sm">{step.unit}</span>
                                                        {measurements[step.id] && (
                                                            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* Swap Legs and Swap Measures Order Buttons - Hidden on mobile */}
                        <div className="hidden md:flex justify-center gap-3">
                            <button
                                onClick={handleSwapLegsToggle}
                                disabled={isReadOnly}
                                className={`mt-2 flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border transition-all ${swapLegSides
                                    ? 'bg-green-100 border-green-400 text-green-700'
                                    : 'bg-gray-100 border-gray-300 text-gray-700'
                                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title={t('legMeasuringPage.swapLegsTitle')}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <span>{swapLegSides ? t('legMeasuringPage.legsSwapped') : t('legMeasuringPage.swapLegs')}</span>
                            </button>

                            <button
                                onClick={handleSwapMeasuresOrderToggle}
                                disabled={isReadOnly}
                                className={`mt-2 flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg border transition-all ${swapMeasuresOrder
                                    ? 'bg-purple-100 border-purple-400 text-purple-700'
                                    : 'bg-gray-100 border-gray-300 text-gray-700'
                                    } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title="Reverse measurement order (start from cA)"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                                <span>{swapMeasuresOrder ? 'Reversed Order' : 'Swap Measures Order'}</span>
                            </button>
                        </div>

                        {!isReadOnly && (
                            <div role="status" aria-live="polite" className={`sticky top-0 z-10 mt-4 mb-6 border-l-4 px-4 py-3 text-sm shadow-sm ${guidanceColorClasses}`}>
                                {currentStep >= 0 && currentStep < measurementSteps.length && currentMessage?.stepIndex !== currentStep
                                    ? getStepPrompt(measurementSteps[currentStep])
                                    : currentMessage?.message || (currentStep >= 0 && currentStep < measurementSteps.length
                                        ? getStepPrompt(measurementSteps[currentStep])
                                        : '')}
                            </div>
                        )}

                        {/* Three Column Layout for Legs - Vertical on mobile */}
                        <div className="flex flex-col gap-6 md:grid md:grid-cols-3 md:gap-6">
                            {/* First Leg Column (Right or Left based on swap preference) */}
                            <div>
                                <h3 className={`text-lg font-semibold mb-3 ${swapLegSides ? 'text-purple-600' : 'text-blue-600'}`}>
                                    {swapLegSides ? t('measurements.legMeasuring.leftLeg') : t('measurements.legMeasuring.rightLeg')} ({getCompletedCount(swapLegSides ? 'left' : 'right')}/{getTotalCount(swapLegSides ? 'left' : 'right')})
                                </h3>

                                {/* Spacing to align cG with lG when AT length type is selected */}
                                {measurements.lengthType === 'AT' && (
                                    <div className="h-24"></div>
                                )}

                                <div className="space-y-2 mb-6">
                                    {measurementSteps
                                        .filter((step) => step.section === (swapLegSides ? 'left' : 'right') && isFieldVisible(step.id))
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                ref={(el) => inputFieldRefs.current[step.id] = el}
                                                className={`p-2 rounded border text-sm ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? `${swapLegSides ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-400' : 'bg-blue-50 border-blue-300 ring-2 ring-blue-400'}`
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-xs flex items-center">
                                                        <span className="md:hidden">{getShortLabel(step.label)} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <span className="hidden md:inline">{step.label} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <MeasurementInfo infoKey={step.infoKey} />
                                                    </span>
                                                    <div className="grid grid-cols-[5rem_5rem_auto] items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            {measurementType === 'madeToMeasure' && (
                                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">HM</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                placeholder={step.unit}
                                                                value={measurements[step.id] || ''}
                                                                className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                                    ? `${swapLegSides ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-400 shadow-lg' : 'bg-blue-50 border-blue-500 ring-2 ring-blue-400 shadow-lg'}`
                                                                    : measurements[step.id]
                                                                        ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                        : 'border-gray-300'
                                                                    } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                                onFocus={() => handleFieldFocus(step.id)}
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>
                                                        {shouldShowZmInputs(step.id) && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">ZM{getZmTension(step.id) && ` ${getZmTension(step.id)}`}{isConditionalZmRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    placeholder={step.unit}
                                                                    value={measurements2[step.id] || ''}
                                                                    className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${measurements2[step.id]
                                                                        ? 'bg-white border-purple-400 text-purple-700 font-semibold'
                                                                        : 'border-purple-200'
                                                                        } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                    onChange={(e) => handleMeasurements2Input(step.id, e.target.value)}
                                                                    onFocus={() => handleFieldFocus(step.id)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                        {measurements[step.id] && (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>

                                {/* Foot Section */}
                                <h4 className={`text-md font-semibold mb-2 ${swapLegSides ? 'text-purple-600' : 'text-blue-600'}`}>
                                    {swapLegSides ? t('measurements.legMeasuring.leftLeg') : t('measurements.legMeasuring.rightLeg')} {t('legMeasuringPage.footLabel')} ({getCompletedCount(swapLegSides ? 'leftFoot' : 'rightFoot')}/{getTotalCount(swapLegSides ? 'leftFoot' : 'rightFoot')})
                                </h4>

                                <div className="space-y-2">
                                    {measurementSteps
                                        .filter((step) => step.section === (swapLegSides ? 'leftFoot' : 'rightFoot') && isFieldVisible(step.id))
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                ref={(el) => inputFieldRefs.current[step.id] = el}
                                                className={`p-2 rounded border text-sm ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? `${swapLegSides ? 'bg-purple-50 border-purple-300 ring-2 ring-purple-400' : 'bg-blue-50 border-blue-300 ring-2 ring-blue-400'}`
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-xs flex items-center">
                                                        <span className="md:hidden">{getShortLabel(step.label)} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <span className="hidden md:inline">{step.label} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <MeasurementInfo infoKey={step.infoKey} />
                                                    </span>
                                                    <div className="grid grid-cols-[5rem_5rem_auto] items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            {measurementType === 'madeToMeasure' && (
                                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">HM</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                placeholder={step.unit}
                                                                value={measurements[step.id] || ''}
                                                                className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                                    ? `${swapLegSides ? 'bg-purple-50 border-purple-500 ring-2 ring-purple-400 shadow-lg' : 'bg-blue-50 border-blue-500 ring-2 ring-blue-400 shadow-lg'}`
                                                                    : measurements[step.id]
                                                                        ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                        : 'border-gray-300'
                                                                    } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                                onFocus={() => handleFieldFocus(step.id)}
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>
                                                        {shouldShowZmInputs(step.id) && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">ZM{getZmTension(step.id) && ` ${getZmTension(step.id)}`}{isConditionalZmRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    placeholder={step.unit}
                                                                    value={measurements2[step.id] || ''}
                                                                    className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${measurements2[step.id]
                                                                        ? 'bg-white border-purple-400 text-purple-700 font-semibold'
                                                                        : 'border-purple-200'
                                                                        } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                    onChange={(e) => handleMeasurements2Input(step.id, e.target.value)}
                                                                    onFocus={() => handleFieldFocus(step.id)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                        {measurements[step.id] && (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>

                            {/* Middle Column - Length Measurements */}
                            <div>
                                <div className="mb-3">
                                    <h3 className="text-lg font-semibold text-green-600 text-center">
                                        {t('legMeasuringPage.lengthMeasurements')} ({getCompletedCount('length')}/{getTotalCount('length')})
                                    </h3>
                                </div>

                                {/* Spacing to match SVG diagram height in right/left columns 
                                <div className="h-[204px] mb-4"></div>
                                */}

                                <div className="space-y-2 mb-6">
                                    {measurementSteps
                                        .filter((step) => step.section === 'length' && isFieldVisible(step.id))
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                ref={(el) => inputFieldRefs.current[step.id] = el}
                                                className={`p-2 rounded border text-sm ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? 'bg-green-50 border-green-300 ring-2 ring-green-400'
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-xs flex items-center">
                                                        <span className="md:hidden">{getShortLabel(step.label)} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <span className="hidden md:inline">{step.label} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <MeasurementInfo infoKey={step.infoKey} />
                                                    </span>
                                                    <div className="grid grid-cols-[5rem_5rem_auto] items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            {measurementType === 'madeToMeasure' && (
                                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">LEFT</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                placeholder={step.unit}
                                                                value={measurements[step.id] || ''}
                                                                className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                                    ? 'bg-green-50 border-green-500 ring-2 ring-green-400 shadow-lg'
                                                                    : measurements[step.id]
                                                                        ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                        : 'border-gray-300'
                                                                    }`}
                                                                onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                                onFocus={() => handleFieldFocus(step.id)}
                                                            />
                                                        </div>
                                                        {measurementType === 'madeToMeasure' && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">RIGHT</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    placeholder={step.unit}
                                                                    value={measurements2[step.id] || ''}
                                                                    className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${measurements2[step.id]
                                                                        ? 'bg-white border-purple-400 text-purple-700 font-semibold'
                                                                        : 'border-purple-200'
                                                                        }`}
                                                                    onChange={(e) => handleMeasurements2Input(step.id, e.target.value)}
                                                                    onFocus={() => handleFieldFocus(step.id)}
                                                                />
                                                            </div>
                                                        )}
                                                        {measurements[step.id] && (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>

                            {/* Third Leg Column (Left or Right based on swap preference) */}
                            <div>
                                <h3 className={`text-lg font-semibold mb-3 ${swapLegSides ? 'text-blue-600' : 'text-purple-600'}`}>
                                    {swapLegSides ? t('measurements.legMeasuring.rightLeg') : t('measurements.legMeasuring.leftLeg')} ({getCompletedCount(swapLegSides ? 'right' : 'left')}/{getTotalCount(swapLegSides ? 'right' : 'left')})
                                </h3>

                                {/* Spacing to align cG with lG when AT length type is selected */}
                                {measurements.lengthType === 'AT' && (
                                    <div className="h-24"></div>
                                )}

                                <div className="space-y-2 mb-6">
                                    {measurementSteps
                                        .filter((step) => step.section === (swapLegSides ? 'right' : 'left') && isFieldVisible(step.id))
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                ref={(el) => inputFieldRefs.current[step.id] = el}
                                                className={`p-2 rounded border text-sm ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? `${swapLegSides ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-400' : 'bg-purple-50 border-purple-300 ring-2 ring-purple-400'}`
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-xs flex items-center">
                                                        <span className="md:hidden">{getShortLabel(step.label)} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <span className="hidden md:inline">{step.label} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <MeasurementInfo infoKey={step.infoKey} />
                                                    </span>
                                                    <div className="grid grid-cols-[5rem_5rem_auto] items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            {measurementType === 'madeToMeasure' && (
                                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">HM</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                placeholder={step.unit}
                                                                value={measurements[step.id] || ''}
                                                                className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                                    ? `${swapLegSides ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-400 shadow-lg' : 'bg-purple-50 border-purple-500 ring-2 ring-purple-400 shadow-lg'}`
                                                                    : measurements[step.id]
                                                                        ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                        : 'border-gray-300'
                                                                    } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                                onFocus={() => handleFieldFocus(step.id)}
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>
                                                        {shouldShowZmInputs(step.id) && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">ZM{getZmTension(step.id) && ` ${getZmTension(step.id)}`}{isConditionalZmRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    placeholder={step.unit}
                                                                    value={measurements2[step.id] || ''}
                                                                    className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${measurements2[step.id]
                                                                        ? 'bg-white border-purple-400 text-purple-700 font-semibold'
                                                                        : 'border-purple-200'
                                                                        } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                    onChange={(e) => handleMeasurements2Input(step.id, e.target.value)}
                                                                    onFocus={() => handleFieldFocus(step.id)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                        {measurements[step.id] && (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>

                                {/* Foot Section */}
                                <h4 className={`text-md font-semibold mb-2 ${swapLegSides ? 'text-blue-600' : 'text-purple-600'}`}>
                                    {swapLegSides ? t('measurements.legMeasuring.rightLeg') : t('measurements.legMeasuring.leftLeg')} {t('legMeasuringPage.footLabel')} ({getCompletedCount(swapLegSides ? 'rightFoot' : 'leftFoot')}/{getTotalCount(swapLegSides ? 'rightFoot' : 'leftFoot')})
                                </h4>

                                <div className="space-y-2">
                                    {measurementSteps
                                        .filter((step) => step.section === (swapLegSides ? 'rightFoot' : 'leftFoot') && isFieldVisible(step.id))
                                        .map((step) => (
                                            <div
                                                key={step.id}
                                                ref={(el) => inputFieldRefs.current[step.id] = el}
                                                className={`p-2 rounded border text-sm ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                    ? `${swapLegSides ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-400' : 'bg-purple-50 border-purple-300 ring-2 ring-purple-400'}`
                                                    : measurements[step.id]
                                                        ? 'bg-green-50 border-green-300'
                                                        : 'bg-gray-50 border-gray-200'
                                                    }`}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <span className="font-medium text-xs flex items-center">
                                                        <span className="md:hidden">{getShortLabel(step.label)} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <span className="hidden md:inline">{step.label} {isFieldRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                        <MeasurementInfo infoKey={step.infoKey} />
                                                    </span>
                                                    <div className="grid grid-cols-[5rem_5rem_auto] items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                            {measurementType === 'madeToMeasure' && (
                                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">HM</span>
                                                            )}
                                                            <input
                                                                type="number"
                                                                step="0.1"
                                                                placeholder={step.unit}
                                                                value={measurements[step.id] || ''}
                                                                className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${currentStep >= 0 && measurementSteps[currentStep]?.id === step.id
                                                                    ? `${swapLegSides ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-400 shadow-lg' : 'bg-purple-50 border-purple-500 ring-2 ring-purple-400 shadow-lg'}`
                                                                    : measurements[step.id]
                                                                        ? 'bg-white border-green-500 text-green-700 font-semibold'
                                                                        : 'border-gray-300'
                                                                    } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                onChange={(e) => handleManualInput(step.id, e.target.value)}
                                                                onFocus={() => handleFieldFocus(step.id)}
                                                                disabled={isReadOnly}
                                                            />
                                                        </div>
                                                        {shouldShowZmInputs(step.id) && (
                                                            <div className="flex items-center gap-1">
                                                                <span className="text-xs font-bold text-purple-600 uppercase tracking-wide">ZM{getZmTension(step.id) && ` ${getZmTension(step.id)}`}{isConditionalZmRequired(step.id) && <span className="text-red-500">*</span>}</span>
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    placeholder={step.unit}
                                                                    value={measurements2[step.id] || ''}
                                                                    className={`w-14 px-1 py-0.5 border rounded text-xs transition-all ${measurements2[step.id]
                                                                        ? 'bg-white border-purple-400 text-purple-700 font-semibold'
                                                                        : 'border-purple-200'
                                                                        } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                                    onChange={(e) => handleMeasurements2Input(step.id, e.target.value)}
                                                                    onFocus={() => handleFieldFocus(step.id)}
                                                                    disabled={isReadOnly}
                                                                />
                                                            </div>
                                                        )}
                                                        {measurements[step.id] && (
                                                            <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - AI Chat Interface */}
                    <div className="w-full md:w-1/3 h-auto md:h-full flex flex-col bg-white md:border-l border-gray-200">
                        {/* Leg Diagram - Hidden on mobile */}
                        <div className="hidden md:flex flex-1 min-h-0 overflow-hidden bg-gradient-to-b from-blue-50 to-white border-b border-gray-200 flex-row justify-center items-center py-2 px-2 gap-2">
                            <svg viewBox="0 -40 250 780" className="h-full max-h-[28rem] w-auto" aria-label="Leg measurement diagram">
                                {/* Leg outline */}
                                <ellipse cx="125" cy="10" rx="60" ry="22" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 65 10 L 72 140 L 90 560" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 185 10 L 178 140 L 160 310" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 70 310 Q 125 305 160 310" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 70 360 Q 125 365 160 360" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 70 310 L 70 360" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 160 310 L 160 360" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 160 360 Q 200 385 160 700" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 10 700 Q 125 685 160 700" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 10 675 Q 5 685 10 700" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 10 675 Q 105 685 90 560" stroke={legOutlineColor} strokeWidth="2.5" fill="none" />
                                {/* cG */}
                                <ellipse cx="125" cy="60" rx="65" ry="16"
                                    stroke={activeLegMeasure === 'cG' ? '#f59e0b' : '#e74c3c'}
                                    strokeWidth={activeLegMeasure === 'cG' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cG' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="30" y1="60" x2="60" y2="60"
                                    stroke={activeLegMeasure === 'cG' ? '#f59e0b' : '#e74c3c'} strokeWidth={activeLegMeasure === 'cG' ? 3 : 2} />
                                <text x="10" y="65" fontSize="20"
                                    fill={activeLegMeasure === 'cG' ? '#f59e0b' : '#e74c3c'}
                                    fontWeight={activeLegMeasure === 'cG' ? '900' : 'bold'}>cG</text>
                                {/* cF */}
                                <ellipse cx="125" cy="135" rx="65" ry="16"
                                    stroke={activeLegMeasure === 'cF' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cF' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cF' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="30" y1="135" x2="60" y2="135"
                                    stroke={activeLegMeasure === 'cF' ? '#f59e0b' : '#3498db'} strokeWidth={activeLegMeasure === 'cF' ? 3 : 2} />
                                <text x="10" y="140" fontSize="20"
                                    fill={activeLegMeasure === 'cF' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cF' ? '900' : 'bold'}>cF</text>
                                {/* cE */}
                                <ellipse cx="125" cy="300" rx="55" ry="16"
                                    stroke={activeLegMeasure === 'cE' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cE' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cE' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="30" y1="300" x2="70" y2="300"
                                    stroke={activeLegMeasure === 'cE' ? '#f59e0b' : '#3498db'} strokeWidth={activeLegMeasure === 'cE' ? 3 : 2} />
                                <text x="10" y="305" fontSize="20"
                                    fill={activeLegMeasure === 'cE' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cE' ? '900' : 'bold'}>cE</text>
                                {/* cD */}
                                <ellipse cx="125" cy="375" rx="55" ry="16"
                                    stroke={activeLegMeasure === 'cD' ? '#f59e0b' : '#008000'}
                                    strokeWidth={activeLegMeasure === 'cD' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cD' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="30" y1="375" x2="70" y2="375"
                                    stroke={activeLegMeasure === 'cD' ? '#f59e0b' : '#008000'} strokeWidth={activeLegMeasure === 'cD' ? 3 : 2} />
                                <text x="10" y="380" fontSize="20"
                                    fill={activeLegMeasure === 'cD' ? '#f59e0b' : '#008000'}
                                    fontWeight={activeLegMeasure === 'cD' ? '900' : 'bold'}>cD</text>
                                {/* cC */}
                                <ellipse cx="135" cy="465" rx="55" ry="16"
                                    stroke={activeLegMeasure === 'cC' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cC' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cC' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="30" y1="465" x2="80" y2="465"
                                    stroke={activeLegMeasure === 'cC' ? '#f59e0b' : '#3498db'} strokeWidth={activeLegMeasure === 'cC' ? 3 : 2} />
                                <text x="10" y="470" fontSize="20"
                                    fill={activeLegMeasure === 'cC' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cC' ? '900' : 'bold'}>cC</text>
                                {/* cB1 */}
                                <ellipse cx="135" cy="515" rx="55" ry="16"
                                    stroke={activeLegMeasure === 'cB1' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cB1' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cB1' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="40" y1="515" x2="80" y2="515"
                                    stroke={activeLegMeasure === 'cB1' ? '#f59e0b' : '#3498db'} strokeWidth={activeLegMeasure === 'cB1' ? 3 : 2} />
                                <text x="10" y="520" fontSize="20"
                                    fill={activeLegMeasure === 'cB1' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cB1' ? '900' : 'bold'}>cB1</text>
                                {/* cB */}
                                <ellipse cx="135" cy="585" rx="50" ry="16"
                                    stroke={activeLegMeasure === 'cB' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cB' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cB' ? 'rgba(251,191,36,0.25)' : 'none'} />
                                <line x1="30" y1="585" x2="85" y2="585"
                                    stroke={activeLegMeasure === 'cB' ? '#f59e0b' : '#3498db'} strokeWidth={activeLegMeasure === 'cB' ? 3 : 2} />
                                <text x="10" y="590" fontSize="20"
                                    fill={activeLegMeasure === 'cB' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cB' ? '900' : 'bold'}>cB</text>
                                {/* cY */}
                                <ellipse cx="150" cy="650" rx="55" ry="16"
                                    stroke={activeLegMeasure === 'cY' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cY' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cY' ? 'rgba(251,191,36,0.25)' : 'none'}
                                    transform="rotate(45, 125, 625)" />
                                <line x1="30" y1="625" x2="90" y2="625"
                                    stroke={activeLegMeasure === 'cY' ? '#f59e0b' : '#3498db'} strokeWidth={activeLegMeasure === 'cY' ? 3 : 2} />
                                <text x="10" y="630" fontSize="20"
                                    fill={activeLegMeasure === 'cY' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cY' ? '900' : 'bold'}>cY</text>
                                {/* cA */}
                                <ellipse cx="50" cy="765" rx="25" ry="12"
                                    stroke={activeLegMeasure === 'cA' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLegMeasure === 'cA' ? 4 : 2.5}
                                    fill={activeLegMeasure === 'cA' ? 'rgba(251,191,36,0.25)' : 'none'}
                                    transform="rotate(45, 125, 695)" />
                                <text x="10" y="690" fontSize="20"
                                    fill={activeLegMeasure === 'cA' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLegMeasure === 'cA' ? '900' : 'bold'}>cA</text>
                            </svg>
                            {/* Lengths Diagram */}
                            <svg viewBox="0 -40 250 780" className="h-full max-h-[28rem] w-auto" aria-label="Leg length diagram">
                                {/* Leg outline */}
                                <ellipse cx="125" cy="10" rx="60" ry="22" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 65 10 L 72 140 L 90 560" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 185 10 L 178 140 L 160 310" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 70 310 Q 125 305 160 310" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 70 360 Q 125 365 160 360" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 70 310 L 70 360" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 160 310 L 160 360" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 160 360 Q 200 385 160 700" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 10 700 Q 125 685 160 700" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 10 675 Q 5 685 10 700" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                <path d="M 10 675 Q 105 685 90 560" stroke={lengthOutlineColor} strokeWidth="2.5" fill="none" />
                                {/* lT */}
                                <line x1="140" y1="10" x2="140" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lT' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lT' ? '#f59e0b' : '#e74c3c'}
                                    strokeWidth={activeLengthMeasure === 'lT' ? 4 : 1} />
                                <line x1="40" y1="10" x2="140" y2="10"
                                    stroke={activeLengthMeasure === 'lT' ? '#f59e0b' : '#e74c3c'}
                                    strokeWidth={activeLengthMeasure === 'lT' ? 3 : 1.5} />
                                <text x="10" y="15" fontSize="20"
                                    fill={activeLengthMeasure === 'lT' ? '#f59e0b' : '#e74c3c'}
                                    fontWeight={activeLengthMeasure === 'lT' ? '900' : 'bold'}>lT</text>
                                {/* lH */}
                                <line x1="135" y1="35" x2="135" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lH' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lH' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lH' ? 4 : 1} />
                                <line x1="40" y1="35" x2="135" y2="35"
                                    stroke={activeLengthMeasure === 'lH' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lH' ? 3 : 1.5} />
                                <text x="10" y="40" fontSize="20"
                                    fill={activeLengthMeasure === 'lH' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lH' ? '900' : 'bold'}>lH</text>
                                {/* lG */}
                                <line x1="130" y1="60" x2="130" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lG' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lG' ? '#f59e0b' : '#e74c3c'}
                                    strokeWidth={activeLengthMeasure === 'lG' ? 4 : 1} />
                                <line x1="40" y1="60" x2="130" y2="60"
                                    stroke={activeLengthMeasure === 'lG' ? '#f59e0b' : '#e74c3c'}
                                    strokeWidth={activeLengthMeasure === 'lG' ? 3 : 1.5} />
                                <text x="10" y="65" fontSize="20"
                                    fill={activeLengthMeasure === 'lG' ? '#f59e0b' : '#e74c3c'}
                                    fontWeight={activeLengthMeasure === 'lG' ? '900' : 'bold'}>lG</text>
                                {/* lF */}
                                <line x1="125" y1="135" x2="125" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lF' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lF' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lF' ? 4 : 1} />
                                <line x1="40" y1="135" x2="125" y2="135"
                                    stroke={activeLengthMeasure === 'lF' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lF' ? 3 : 1.5} />
                                <text x="10" y="140" fontSize="20"
                                    fill={activeLengthMeasure === 'lF' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lF' ? '900' : 'bold'}>lF</text>
                                {/* lE */}
                                <line x1="120" y1="300" x2="120" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lE' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lE' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lE' ? 4 : 1} />
                                <line x1="40" y1="300" x2="120" y2="300"
                                    stroke={activeLengthMeasure === 'lE' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lE' ? 3 : 1.5} />
                                <text x="10" y="305" fontSize="20"
                                    fill={activeLengthMeasure === 'lE' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lE' ? '900' : 'bold'}>lE</text>
                                {/* lD */}
                                <line x1="115" y1="375" x2="115" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lD' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lD' ? '#f59e0b' : '#008000'}
                                    strokeWidth={activeLengthMeasure === 'lD' ? 4 : 1} />
                                <line x1="40" y1="375" x2="115" y2="375"
                                    stroke={activeLengthMeasure === 'lD' ? '#f59e0b' : '#008000'}
                                    strokeWidth={activeLengthMeasure === 'lD' ? 3 : 1.5} />
                                <text x="10" y="380" fontSize="20"
                                    fill={activeLengthMeasure === 'lD' ? '#f59e0b' : '#008000'}
                                    fontWeight={activeLengthMeasure === 'lD' ? '900' : 'bold'}>lD</text>
                                {/* lC */}
                                <line x1="110" y1="465" x2="110" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lC' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lC' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lC' ? 4 : 1} />
                                <line x1="40" y1="465" x2="110" y2="465"
                                    stroke={activeLengthMeasure === 'lC' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lC' ? 3 : 1.5} />
                                <text x="10" y="470" fontSize="20"
                                    fill={activeLengthMeasure === 'lC' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lC' ? '900' : 'bold'}>lC</text>
                                {/* lB1 */}
                                <line x1="105" y1="515" x2="105" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lB1' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lB1' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lB1' ? 4 : 1} />
                                <line x1="40" y1="515" x2="105" y2="515"
                                    stroke={activeLengthMeasure === 'lB1' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lB1' ? 3 : 1.5} />
                                <text x="10" y="520" fontSize="20"
                                    fill={activeLengthMeasure === 'lB1' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lB1' ? '900' : 'bold'}>lB1</text>
                                {/* lB */}
                                <line x1="100" y1="585" x2="100" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lB' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lB' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lB' ? 4 : 1} />
                                <line x1="40" y1="585" x2="100" y2="585"
                                    stroke={activeLengthMeasure === 'lB' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lB' ? 3 : 1.5} />
                                <text x="10" y="590" fontSize="20"
                                    fill={activeLengthMeasure === 'lB' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lB' ? '900' : 'bold'}>lB</text>
                                {/* lA */}
                                <line x1="40" y1="650" x2="40" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lA' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lA' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lA' ? 4 : 1} />
                                <line x1="40" y1="700" x2="160" y2="700"
                                    stroke={activeLengthMeasure === 'lA' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lA' ? 3 : 1.5} />
                                <text x="10" y="655" fontSize="20"
                                    fill={activeLengthMeasure === 'lA' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lA' ? '900' : 'bold'}>lA</text>
                                {/* lZ */}
                                <line x1="5" y1="685" x2="5" y2="700"
                                    strokeDasharray={activeLengthMeasure === 'lZ' ? 'none' : '4 4'}
                                    stroke={activeLengthMeasure === 'lZ' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lZ' ? 4 : 1} />
                                <line x1="5" y1="700" x2="160" y2="700"
                                    stroke={activeLengthMeasure === 'lZ' ? '#f59e0b' : '#3498db'}
                                    strokeWidth={activeLengthMeasure === 'lZ' ? 3 : 1.5} />
                                <text x="10" y="690" fontSize="20"
                                    fill={activeLengthMeasure === 'lZ' ? '#f59e0b' : '#3498db'}
                                    fontWeight={activeLengthMeasure === 'lZ' ? '900' : 'bold'}>lZ</text>
                            </svg>
                        </div>

                        {/* Voice Controls - Smaller on mobile */}
                        <div className="flex-shrink-0 p-2 md:p-4 bg-white border-t">
                            <div className="flex items-center justify-center space-x-2 md:space-x-4 mb-2 md:mb-3">
                                <button
                                    onClick={isListening ? stopVoiceRecognition : startVoiceRecognition}
                                    onTouchStart={(e) => {
                                        // Prevent default to avoid double-firing on touch devices
                                        e.preventDefault();
                                        if (isListening) {
                                            stopVoiceRecognition();
                                        } else {
                                            startVoiceRecognition();
                                        }
                                    }}
                                    disabled={isReadOnly}
                                    className={`w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center transition-all ${isListening
                                        ? 'bg-red-600 hover:bg-red-700 recording-pulse'
                                        : 'bg-blue-600 hover:bg-blue-700'
                                        } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    aria-label={isListening ? 'Stop recording' : 'Start recording'}
                                >
                                    {isListening ? (
                                        <svg className="w-5 h-5 md:w-7 md:h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                            <rect x="6" y="6" width="12" height="12" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5 md:w-7 md:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                        </svg>
                                    )}
                                </button>

                                <button
                                    onClick={handleSave}
                                    disabled={!areAllMeasurementsComplete() || isReadOnly}
                                    className={`px-3 py-1.5 md:px-5 md:py-2.5 text-xs md:text-sm ${!areAllMeasurementsComplete() || isReadOnly
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'btn-primary'
                                        }`}
                                >
                                    <span className="hidden md:inline">{t('legMeasuringPage.saveAndProductSearch')}</span>
                                    <span className="md:hidden">{t('legMeasuringPage.saveAndSearch')}</span>
                                </button>
                            </div>

                            {/* Navigation Buttons - Smaller on mobile */}
                            {(isReadOnly || measurementId) && (
                                <div className="mt-2 md:mt-3 pt-2 md:pt-3 border-t flex justify-between">
                                    <button
                                        onClick={handleNavigateToPatientInfo}
                                        className="px-2 py-1 md:px-3 md:py-1.5 text-xs border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                                    >
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span className="hidden md:inline">{t('patientInfo.progress.step1')}</span>
                                        <span className="md:hidden">{t('common.back')}</span>
                                    </button>
                                    <button
                                        onClick={handleNavigateToStockingSelection}
                                        className="px-2 py-1 md:px-3 md:py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                    >
                                        <span className="hidden md:inline">{t('measurements.stockingSelection.selectedProduct')}</span>
                                        <span className="md:hidden">{t('measurements.stockingSelection.title')}</span>
                                        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            )}

                            <p className="hidden md:block text-center text-xs text-gray-500 mt-3">
                                {isReadOnly
                                    ? t('legMeasuringPage.readOnlyStatus')
                                    : areAllMeasurementsComplete() && currentStep < measurementSteps.length
                                        ? t('legMeasuringPage.allMeasurementsComplete')
                                        : isListening
                                            ? t('legMeasuringPage.listeningPrompt')
                                            : t('legMeasuringPage.clickMicPrompt')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LegMeasuring;
